import PrintMethodToggle from '@/components/print/PrintMethodToggle';
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Play, Package, Check, CheckCircle, Clock, RefreshCw, Plus, AlertCircle, Printer, ChevronDown, ChevronUp, Calculator, Boxes, RotateCcw, Usb, Settings, AlertTriangle, XCircle, Warehouse, UserPlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';
import { noRateMessage, usePricingChannels } from '@/hooks/usePricingChannels';
import * as printerApi from '@/services/printerApi';
import { generateTsplFromLabelData, PRINTER_CONFIGS } from '@/utils/tsplGenerator';
import { useUSNReplacement } from '@/hooks/useUSNReplacement';
import SmartComboPlanningTab from './SmartComboPlanningTab';

const ComboAssemblyStation = () => {
  const navigate = useNavigate();
  const { openReplaceDialog, replaceBatch } = useUSNReplacement();
  
  // Stats
  const [stats, setStats] = useState({
    active_sessions: 0,
    pending_component_slips: 0,
    pending_labels: 0,
    pending_put_away: 0,
    combos_in_storage: 0
  });
  
  // Sessions
  const [sessions, setSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(true);
  
  // Create session dialog
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [combos, setCombos] = useState([]);
  const [selectedCombo, setSelectedCombo] = useState('');
  const [targetQuantity, setTargetQuantity] = useState(10);
  const [creating, setCreating] = useState(false);
  
  // Cascading filter states for combo selection
  const [designNames, setDesignNames] = useState([]);
  const [shades, setShades] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [descriptions, setDescriptions] = useState([]);
  const [selectedDesignName, setSelectedDesignName] = useState('');
  const [selectedShade, setSelectedShade] = useState('');
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedDescription, setSelectedDescription] = useState('');
  const [detectedCombo, setDetectedCombo] = useState(null);
  const [loadingDropdowns, setLoadingDropdowns] = useState(false);
  
  // Assembly dialog
  const [assemblyDialogOpen, setAssemblyDialogOpen] = useState(false);
  const [activeSession, setActiveSession] = useState(null);
  const [quantityAssembled, setQuantityAssembled] = useState(1);
  const [recording, setRecording] = useState(false);
  
  // Printing & Return
  const [printing, setPrinting] = useState(false);
  const [returningExcess, setReturningExcess] = useState(false);
  
  // USB Printing State
  const [usbDevice, setUsbDevice] = useState(null);
  const [boxPrinterDevice, setBoxPrinterDevice] = useState(null);
  const [usbSupported, setUsbSupported] = useState(false);
  const [printerModel, setPrinterModel] = useState('TSC_TE210');
  const [pricingType, setPricingType] = useState('TN');
  const availablePricingTypes = usePricingChannels();
  const [copiesPerLabel, setCopiesPerLabel] = useState(1);
  const [templates, setTemplates] = useState([]);
  const [arialLineSpacingMultiplier, setArialLineSpacingMultiplier] = useState(2.5);
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const [printDialogSession, setPrintDialogSession] = useState(null);
  const [printProgress, setPrintProgress] = useState('');
  const [showBoxAlert, setShowBoxAlert] = useState(false);
  const [pendingBoxData, setPendingBoxData] = useState(null);
  const [showPrinterSettings, setShowPrinterSettings] = useState(false);
  
  // Wizard state
  const [selectedSessionId, setSelectedSessionId] = useState(null);
  const [sessionDetail, setSessionDetail] = useState(null);
  const [showHistory, setShowHistory] = useState(false);

  // Date filter for sessions (default: today). Applies to both Live & History tabs.
  const [filterDate, setFilterDate] = useState(() => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  });

  // Handover state
  const [handoverSuggestions, setHandoverSuggestions] = useState([]);
  const [showHandoverDialog, setShowHandoverDialog] = useState(false);
  const [handingOver, setHandingOver] = useState(null);
  const [handoverSourceSessionId, setHandoverSourceSessionId] = useState(null);

  // Leftover declaration state (mandatory modal after multi-bundle session completes)
  const [leftoverForm, setLeftoverForm] = useState(null);          // full form payload
  const [leftoverSessionId, setLeftoverSessionId] = useState(null);
  const [leftoverInputs, setLeftoverInputs] = useState({});        // {bundle_code: qty}
  const [submittingLeftover, setSubmittingLeftover] = useState(false);
  const [manualLeftoverMode, setManualLeftoverMode] = useState(false); // true when user opened it manually
  const [loadingLeftoverForm, setLoadingLeftoverForm] = useState(false);

  // Derive selected session and wizard step
  const selectedSession = sessions.find(s => s.session_id === selectedSessionId);
  
  const getWizardStep = (session) => {
    if (!session) return 0;
    if (session.status === 'CANCELLED') return 0;
    if (session.excess_returned) return 5; // Done
    if (session.status === 'COMPLETED') return session.labels_printed ? 4 : 3;
    if (session.status === 'ASSEMBLY_IN_PROGRESS') return 2;
    return 1; // PENDING_COMPONENTS
  };

  const wizardStep = getWizardStep(selectedSession);
  
  // Split sessions into live vs history (cancelled sessions also go to history)
  const liveSessions = sessions.filter(s => !s.excess_returned && s.status !== 'CANCELLED');
  const historySessions = sessions.filter(s => s.excess_returned || s.status === 'CANCELLED');

  // Session search
  const [sessionSearch, setSessionSearch] = useState('');
  const filteredDisplaySessions = (showHistory ? historySessions : liveSessions).filter(s => {
    if (!sessionSearch.trim()) return true;
    const q = sessionSearch.toLowerCase();
    return (
      (s.design_name || '').toLowerCase().includes(q) ||
      (s.shade || '').toLowerCase().includes(q) ||
      (s.size || '').toLowerCase().includes(q) ||
      (s.description || '').toLowerCase().includes(q) ||
      (s.combo_name || '').toLowerCase().includes(q) ||
      (s.combo_barcode || '').toLowerCase().includes(q) ||
      (s.session_id || '').toLowerCase().includes(q) ||
      (s.pp_staff_name || '').toLowerCase().includes(q) ||
      (s.pp_staff_id || '').toLowerCase().includes(q)
    );
  });

  // Excess Return Scanning
  const [excessScanDialog, setExcessScanDialog] = useState(false);
  const [excessScanSession, setExcessScanSession] = useState(null);
  const [excessComponents, setExcessComponents] = useState([]);
  const [scannedReturnUsns, setScannedReturnUsns] = useState({});  // {comp_barcode: Set of scanned USN codes}
  const [excessScanInput, setExcessScanInput] = useState('');
  const [loadingExcessData, setLoadingExcessData] = useState(false);

  // Excess Pieces Overview (standalone)
  const [showExcessOverview, setShowExcessOverview] = useState(false);
  const [excessOverviewData, setExcessOverviewData] = useState([]);
  const [loadingExcessOverview, setLoadingExcessOverview] = useState(false);

  // Excess Return Result (persistent display of new bundle numbers)
  const [excessReturnResult, setExcessReturnResult] = useState(null);

  // Floor Stock status
  const [floorStockCount, setFloorStockCount] = useState(0);
  const [showFloorStock, setShowFloorStock] = useState(false);
  const [floorStockData, setFloorStockData] = useState(null);
  const [loadingFloorStock, setLoadingFloorStock] = useState(false);
  const [releasingFloorStock, setReleasingFloorStock] = useState(false);

  // Cancel session
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelSessionTarget, setCancelSessionTarget] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);

  // Assign component picker
  const [assignPickerDialogOpen, setAssignPickerDialogOpen] = useState(false);
  const [assignPickerTarget, setAssignPickerTarget] = useState(null); // { session_id, component_barcode, component_name }
  const [sfgPickers, setSfgPickers] = useState([]);
  const [loadingSfgPickers, setLoadingSfgPickers] = useState(false);
  const [assigningPicker, setAssigningPicker] = useState(false);
  const [compPickerSearch, setCompPickerSearch] = useState('');

  const fetchExcessOverview = useCallback(async () => {
    setLoadingExcessOverview(true);
    try {
      const resp = await apiClient.get('/combo/assembly/excess-pieces-overview');
      setExcessOverviewData(resp.data.sessions || []);
    } catch (err) {
      console.error('Failed to load excess overview:', err);
      toast.error('Failed to load excess pieces data');
    } finally {
      setLoadingExcessOverview(false);
    }
  }, []);

  const fetchFloorStockStatus = useCallback(async () => {
    setLoadingFloorStock(true);
    try {
      const resp = await apiClient.get('/combo/assembly/floor-stock-status');
      setFloorStockData(resp.data);
      setFloorStockCount(resp.data.total_bundles || 0);
    } catch (err) {
      console.error('Failed to load floor stock:', err);
    } finally {
      setLoadingFloorStock(false);
    }
  }, []);

  const handleReleaseFloorStock = async () => {
    if (!window.confirm('This will mark all stranded PENDING_PUTAWAY bundles from combo returns as STORED, making them available for future sessions. Continue?')) return;
    setReleasingFloorStock(true);
    try {
      const resp = await apiClient.post('/combo/assembly/release-floor-stock');
      toast.success(resp.data.message);
      setFloorStockCount(0);
      fetchFloorStockStatus();
      fetchSessions();
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to release floor stock');
    } finally {
      setReleasingFloorStock(false);
    }
  };

  const handleCancelSession = async () => {
    if (!cancelSessionTarget) return;
    setCancelling(true);
    try {
      const resp = await apiClient.post('/combo/assembly/cancel-session', {
        session_id: cancelSessionTarget.session_id,
        reason: cancelReason
      });
      toast.success(resp.data.message);
      setCancelDialogOpen(false);
      setCancelSessionTarget(null);
      setCancelReason('');
      setSelectedSessionId(null);
      setSessionDetail(null);
      fetchSessions();
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel session');
    } finally {
      setCancelling(false);
    }
  };

  const fetchSfgPickers = useCallback(async () => {
    setLoadingSfgPickers(true);
    try {
      const resp = await apiClient.get('/wms/sfg-pickers');
      setSfgPickers(resp.data.pickers || []);
    } catch (err) {
      console.error('Failed to load SFG pickers:', err);
    } finally {
      setLoadingSfgPickers(false);
    }
  }, []);

  const openAssignPickerDialog = (sessionId, compBarcode, compName) => {
    setAssignPickerTarget({ session_id: sessionId, component_barcode: compBarcode, component_name: compName });
    setAssignPickerDialogOpen(true);
    setCompPickerSearch('');
    if (sfgPickers.length === 0) fetchSfgPickers();
  };

  const handleAssignComponentPicker = async (staffId) => {
    if (!assignPickerTarget) return;
    setAssigningPicker(true);
    try {
      const resp = await apiClient.post('/combo/assembly/assign-component-picker', {
        session_id: assignPickerTarget.session_id,
        component_barcode: assignPickerTarget.component_barcode,
        staff_id: staffId
      });
      toast.success(resp.data.message);
      setAssignPickerDialogOpen(false);
      setAssignPickerTarget(null);
      setCompPickerSearch('');
      fetchSessions();
      if (selectedSessionId) selectSession(selectedSessionId);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to assign picker');
    } finally {
      setAssigningPicker(false);
    }
  };

  const fetchStats = useCallback(async () => {
    try {
      const response = await apiClient.get('/combo/assembly/stats');
      setStats(response.data.stats || {});
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, []);

  const fetchSessions = useCallback(async () => {
    try {
      setLoadingSessions(true);
      const params = { limit: 500 };
      if (filterDate) params.date = filterDate;
      const response = await apiClient.get('/combo/assembly/sessions', { params });
      setSessions(response.data.sessions || []);
      const pending = response.data.floor_stock_pending || 0;
      const unreturned = response.data.unreturned_excess_sessions || 0;
      setFloorStockCount(pending + unreturned);
    } catch (error) {
      console.error('Error fetching sessions:', error);
      toast.error('Failed to fetch sessions');
    } finally {
      setLoadingSessions(false);
    }
  }, [filterDate]);

  const fetchCombos = useCallback(async () => {
    try {
      const response = await apiClient.get('/combo/bom/list', {
        params: { is_active: true, limit: 500 }
      });
      setCombos(response.data.combos || []);
    } catch (error) {
      console.error('Error fetching combos:', error);
    }
  }, []);

  // Fetch design names for cascading filter
  const fetchDesignNames = useCallback(async () => {
    try {
      const response = await apiClient.get('/combo/bom/dropdowns/design-names');
      setDesignNames(response.data.design_names || []);
    } catch (error) {
      console.error('Error fetching design names:', error);
    }
  }, []);

  // Fetch shades based on selected design
  const fetchShades = useCallback(async (designName) => {
    if (!designName) {
      setShades([]);
      return;
    }
    try {
      setLoadingDropdowns(true);
      const response = await apiClient.get('/combo/bom/dropdowns/shades', {
        params: { design_name: designName }
      });
      setShades(response.data.shades || []);
    } catch (error) {
      console.error('Error fetching shades:', error);
    } finally {
      setLoadingDropdowns(false);
    }
  }, []);

  // Fetch sizes based on selected design and shade
  const fetchSizes = useCallback(async (designName, shade) => {
    if (!designName || !shade) {
      setSizes([]);
      return;
    }
    try {
      setLoadingDropdowns(true);
      const response = await apiClient.get('/combo/bom/dropdowns/sizes', {
        params: { design_name: designName, shade: shade }
      });
      setSizes(response.data.sizes || []);
    } catch (error) {
      console.error('Error fetching sizes:', error);
    } finally {
      setLoadingDropdowns(false);
    }
  }, []);

  // Fetch descriptions based on selected design, shade, and size
  const fetchDescriptions = useCallback(async (designName, shade, size) => {
    if (!designName || !shade || !size) {
      setDescriptions([]);
      return;
    }
    try {
      setLoadingDropdowns(true);
      const response = await apiClient.get('/combo/bom/dropdowns/descriptions', {
        params: { design_name: designName, shade: shade, size: size }
      });
      setDescriptions(response.data.descriptions || []);
    } catch (error) {
      console.error('Error fetching descriptions:', error);
    } finally {
      setLoadingDropdowns(false);
    }
  }, []);

  // Detect combo barcode based on all selections
  const detectComboBarcode = useCallback(async (designName, shade, size, description) => {
    if (!designName || !shade || !size || !description) {
      setDetectedCombo(null);
      setSelectedCombo('');
      return;
    }
    try {
      setLoadingDropdowns(true);
      const response = await apiClient.get('/combo/bom/detect-barcode', {
        params: { 
          design_name: designName, 
          shade: shade, 
          size: size,
          description: description
        }
      });
      if (response.data.success) {
        setDetectedCombo(response.data);
        setSelectedCombo(response.data.combo_barcode);
      } else {
        setDetectedCombo(null);
        setSelectedCombo('');
        toast.error('No matching combo found');
      }
    } catch (error) {
      console.error('Error detecting combo:', error);
      setDetectedCombo(null);
      setSelectedCombo('');
    } finally {
      setLoadingDropdowns(false);
    }
  }, []);

  // Handle design name change
  const handleDesignNameChange = (value) => {
    setSelectedDesignName(value);
    setSelectedShade('');
    setSelectedSize('');
    setSelectedDescription('');
    setDetectedCombo(null);
    setSelectedCombo('');
    fetchShades(value);
    setSizes([]);
    setDescriptions([]);
  };

  // Handle shade change
  const handleShadeChange = (value) => {
    setSelectedShade(value);
    setSelectedSize('');
    setSelectedDescription('');
    setDetectedCombo(null);
    setSelectedCombo('');
    fetchSizes(selectedDesignName, value);
    setDescriptions([]);
  };

  // Handle size change
  const handleSizeChange = (value) => {
    setSelectedSize(value);
    setSelectedDescription('');
    setDetectedCombo(null);
    setSelectedCombo('');
    fetchDescriptions(selectedDesignName, selectedShade, value);
  };

  // Handle description change
  const handleDescriptionChange = (value) => {
    setSelectedDescription(value);
    detectComboBarcode(selectedDesignName, selectedShade, selectedSize, value);
  };

  // Reset cascading filters when dialog closes
  const handleDialogOpenChange = (open) => {
    setCreateDialogOpen(open);
    if (!open) {
      setSelectedDesignName('');
      setSelectedShade('');
      setSelectedSize('');
      setSelectedDescription('');
      setDetectedCombo(null);
      setSelectedCombo('');
      setShades([]);
      setSizes([]);
      setDescriptions([]);
    }
  };

  useEffect(() => {
    fetchStats();
    fetchSessions();
    fetchCombos();
    fetchDesignNames();
    setUsbSupported(printerApi.isWebUSBSupported());
    fetchTemplates();
    fetchPrintSettings();
  }, [fetchStats, fetchSessions, fetchCombos, fetchDesignNames]);

  const fetchTemplates = async () => {
    try {
      const response = await apiClient.get('/main-labels/templates');
      setTemplates(response.data.templates || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const fetchPrintSettings = async () => {
    try {
      const response = await apiClient.get('/print-settings');
      if (response.data.settings?.arial_line_spacing_multiplier) {
        setArialLineSpacingMultiplier(response.data.settings.arial_line_spacing_multiplier);
      }
    } catch (error) {
      console.error('Error fetching print settings:', error);
    }
  };

  const findTemplateByName = (templateName) => {
    if (!templateName || !templates.length) return null;
    // Normalize: lowercase, collapse multiple spaces, strip spaces around parens
    const normalize = (s) => (s || '').toLowerCase().replace(/\s+/g, ' ').replace(/\s*\(\s*/g, '(').replace(/\s*\)\s*/g, ')').trim();
    const target = normalize(templateName);
    // Exact match first (normalized)
    let match = templates.find(t => normalize(t.template_name) === target);
    if (match) return match;
    // Partial match: target includes template name or vice versa
    match = templates.find(t => target.includes(normalize(t.template_name)) || normalize(t.template_name).includes(target));
    return match || null;
  };

  const connectUSBPrinter = async () => {
    try {
      const device = await printerApi.requestUSBPrinter();
      setUsbDevice(device);
      toast.success(`Connected to ${device.productName || 'USB Printer'} (Product Labels)`);
      return device;
    } catch (error) {
      console.error('Error connecting USB:', error);
      toast.error('Failed to connect USB printer. Please try again.');
      return null;
    }
  };

  const connectBoxPrinter = async () => {
    try {
      const device = await printerApi.requestUSBPrinter();
      setBoxPrinterDevice(device);
      toast.success(`Connected to ${device.productName || 'USB Printer'} (Box Labels)`);
      return device;
    } catch (error) {
      console.error('Error connecting box printer:', error);
      toast.error('Failed to connect box label printer.');
      return null;
    }
  };

  // Execute USB print for a set of combo USNs
  const executeUsbPrint = async (labelDataMap, defaultTemplate, usns, device, printType, comboBarcode) => {
    const printerConfig = PRINTER_CONFIGS[printerModel];
    let allCommands = '';
    let totalLabels = 0;
    const labelTypeText = printType === 'box' ? 'box' : 'product';

    for (const usn of usns) {
      try {
        const cachedData = labelDataMap[usn.barcode || comboBarcode];
        if (!cachedData) continue;

        const labelData = cachedData.label_data || {};
        const mrpFormatted = cachedData.mrp_formatted || `Rs.${labelData.mrp || 0}`;

        if (printType === 'product') {
          const templateName = usn.print_template || 'Standard';
          const template = findTemplateByName(templateName) || defaultTemplate;
          for (let copy = 0; copy < copiesPerLabel; copy++) {
            allCommands += generateTsplFromLabelData(
              labelData, template, usn.usn_code, mrpFormatted,
              usn.usn_code, printerConfig, template?.template_type,
              usn.pair_number, arialLineSpacingMultiplier
            );
            totalLabels++;
          }
        } else if (printType === 'box' && usn.box_template) {
          const boxTemplate = findTemplateByName(usn.box_template);
          const tpl = boxTemplate || defaultTemplate;
          if (tpl) {
            for (let copy = 0; copy < copiesPerLabel; copy++) {
              allCommands += generateTsplFromLabelData(
                labelData, tpl, usn.usn_code, mrpFormatted,
                usn.usn_code, printerConfig, boxTemplate?.template_type || 'box',
                usn.pair_number, arialLineSpacingMultiplier
              );
              totalLabels++;
            }
          }
        }
      } catch (usnError) {
        console.error(`Error generating label for ${usn.usn_code}:`, usnError);
      }
    }

    if (!allCommands) {
      if (printType === 'box') {
        toast.warning('No box labels to print');
        return 0;
      }
      throw new Error('Failed to generate label commands');
    }

    if (device) {
      await printerApi.sendToUSBPrinter(device, allCommands);
    }
    return totalLabels;
  };

  // Main USB print handler for combo sessions
  const handleUsbPrintStickers = async (session, isReprint = false) => {
    // Ensure USB printer is connected
    let device = usbDevice;
    if (!device) {
      device = await connectUSBPrinter();
      if (!device) return;
    }

    setPrinting(true);
    setPrintDialogSession(session);
    setPrintDialogOpen(true);

    try {
      // Step 1: Get combo USNs (unprinted or reprint)
      setPrintProgress(isReprint ? 'Fetching labels for reprint...' : 'Fetching label data...');
      const reprintParam = isReprint ? '?reprint=true' : '';
      const labelResp = await apiClient.get(`/combo/assembly/sessions/${session.session_id}/label-data${reprintParam}`);
      const labelInfo = labelResp.data;

      if (labelInfo.usn_count === 0) {
        toast.info(isReprint ? 'No printed stickers found to reprint' : 'No unprinted stickers found');
        setPrintDialogOpen(false);
        return;
      }

      // REPRINT = REPLACE: deactivate old combo USNs and claim fresh ones from
      // the REPLACE pool. The new USNs inherit barcode/template/pair_number/etc.
      if (isReprint) {
        const oldCodes = labelInfo.usns.map(u => u.usn_code).filter(Boolean);
        const dialog = await openReplaceDialog({
          count: oldCodes.length,
          sourceFlow: 'combo_v1_session_reprint',
          subjectLabel: `Combo session ${session.session_id} — ${oldCodes.length} USN(s)`,
        });
        if (!dialog) { setPrintDialogOpen(false); return; }
        setPrintProgress(`Replacing ${oldCodes.length} USN(s)…`);
        const batch = await replaceBatch(oldCodes, {
          reason: dialog.reason,
          sourceFlow: 'combo_v1_session_reprint',
          linkedDocuments: { session_id: session.session_id, source_page: 'ComboAssemblyStation' },
        });
        if (!batch) { setPrintDialogOpen(false); return; }
        if (batch.failed_count) {
          const firstDetail = batch.failures?.[0]?.detail || 'unknown reason';
          toast.warning(
            batch.failed_count === 1
              ? `Replace failed: ${firstDetail}`
              : `${batch.failed_count} replace failures — first: ${firstDetail}`,
            { duration: 10000 }
          );
          console.warn('USN replace failures:', batch.failures);
        }
        const oldToNew = Object.fromEntries(batch.replacements.map(r => [r.old_usn, r.new_usn]));
        labelInfo.usns = labelInfo.usns.map(u => ({
          ...u,
          usn_code: oldToNew[u.usn_code] || u.usn_code,
        }));
      }

      // Step 2: Fetch batch label data from main-labels API
      const barcodes = [...new Set(labelInfo.usns.map(u => u.barcode).filter(Boolean))];
      setPrintProgress(`Fetching label data for ${barcodes.length} item(s)...`);
      
      // Find the correct template by the item's print_template name
      const itemTemplateName = labelInfo.usns[0]?.print_template || '';
      const matchedTemplate = findTemplateByName(itemTemplateName);
      
      const batchResp = await apiClient.post('/main-labels/preview-batch', {
        barcodes,
        pricing_type: pricingType,
        template_id: matchedTemplate?.template_id || templates[0]?.template_id,
        fallback_barcode: labelInfo.item_barcode
      });
      const noRate = noRateMessage(batchResp.data, pricingType);
      if (noRate) throw new Error(noRate);
      const labelDataMap = batchResp.data.results || {};
      const defaultTemplate = matchedTemplate || batchResp.data.template;

      // Step 3: Print PRODUCT labels
      setPrintProgress(`Printing ${labelInfo.usn_count} product labels...`);
      const productCount = await executeUsbPrint(
        labelDataMap, defaultTemplate, labelInfo.usns, device, 'product', labelInfo.combo_barcode
      );
      toast.success(`Printed ${productCount} product labels`);

      // Step 4: Mark labels as printed (skip for reprints — already marked)
      if (!isReprint && !labelInfo.reprint) {
        setPrintProgress('Updating status...');
        const printedUsns = labelInfo.usns.map(u => u.usn_code);
        await apiClient.post(`/combo/assembly/sessions/${session.session_id}/mark-labels-printed`, {
          session_id: session.session_id,
          combo_usns: printedUsns,
          print_method: 'USB'
        });
      }

      // Step 5: If box template exists, prompt for box labels
      if (labelInfo.has_box_template) {
        setPrintProgress('');
        setPrinting(false);
        setPrintDialogOpen(false);
        setPendingBoxData({ labelInfo, labelDataMap, defaultTemplate });
        setShowBoxAlert(true);
        fetchSessions();
        return;
      }

      setPrintProgress('');
      setPrintDialogOpen(false);
      fetchSessions();
      fetchStats();
    } catch (error) {
      console.error('USB print error:', error);
      toast.error(error.response?.data?.detail || error.message || 'Print failed');
    } finally {
      setPrinting(false);
      setPrintProgress('');
    }
  };

  // Handle box sticker confirmation
  const handleBoxPrintConfirm = async () => {
    setShowBoxAlert(false);
    if (!pendingBoxData) return;

    let printerToUse = boxPrinterDevice;
    if (!printerToUse) {
      toast.info('Please select a printer for box labels');
      printerToUse = await connectBoxPrinter();
      if (!printerToUse) {
        setShowBoxAlert(true);
        return;
      }
    }

    setPrinting(true);
    setPrintProgress('Printing box labels...');
    setPrintDialogOpen(true);

    try {
      const { labelInfo, labelDataMap, defaultTemplate } = pendingBoxData;
      const boxCount = await executeUsbPrint(
        labelDataMap, defaultTemplate, labelInfo.usns, printerToUse, 'box', labelInfo.combo_barcode
      );
      if (boxCount > 0) {
        toast.success(`Printed ${boxCount} box labels`);
      }
    } catch (error) {
      console.error('Box print error:', error);
      toast.error(error.message || 'Box label print failed');
    } finally {
      setPrinting(false);
      setPrintProgress('');
      setPrintDialogOpen(false);
      setPendingBoxData(null);
      fetchSessions();
      fetchStats();
    }
  };

  const handleBoxPrintCancel = () => {
    setShowBoxAlert(false);
    setPendingBoxData(null);
  };

  const handleCreateSession = async () => {
    if (!selectedCombo) {
      toast.error('Please select a combo');
      return;
    }
    if (targetQuantity < 1) {
      toast.error('Target quantity must be at least 1');
      return;
    }
    
    setCreating(true);
    try {
      const response = await apiClient.post('/combo/assembly/start-session', {
        combo_barcode: selectedCombo,
        target_quantity: targetQuantity
      });
      
      toast.success(`Assembly session created: ${response.data.session_id}`);
      setCreateDialogOpen(false);
      setSelectedCombo('');
      setTargetQuantity(10);
      fetchStats();
      fetchSessions();
    } catch (error) {
      console.error('Create session error:', error);
      toast.error(error.response?.data?.detail || 'Failed to create session');
    } finally {
      setCreating(false);
    }
  };

  const handleStartAssembly = async (session) => {
    try {
      await apiClient.post('/combo/assembly/start-assembly', null, {
        params: { session_id: session.session_id }
      });
      toast.success('Assembly started');
      fetchSessions();
    } catch (error) {
      console.error('Start assembly error:', error);
      toast.error(error.response?.data?.detail || 'Failed to start assembly');
    }
  };

  const handleRetryPickSlips = async (session) => {
    try {
      const response = await apiClient.get(`/combo/assembly/fix-session-pick-slips/${session.session_id}`);
      if (response.data.created > 0) {
        toast.success(`Created ${response.data.created} pick slip(s) for missing components`);
        if (response.data.warnings?.length > 0) {
          response.data.warnings.forEach(w => toast.warning(w));
        }
      } else {
        toast.info(response.data.message || 'No new pick slips needed');
      }
      fetchSessions();
    } catch (error) {
      console.error('Retry pick slips error:', error);
      toast.error(error.response?.data?.detail || 'Failed to create pick slips');
    }
  };

  const handleForceCompletePicking = async (session) => {
    if (!window.confirm('This will force-complete the component picking and assign a PP staff. Only use if all bundles were physically picked. Continue?')) return;
    try {
      const response = await apiClient.get(`/wms/combo-pick/force-complete-handover?session_id=${encodeURIComponent(session.session_id)}`);
      if (response.data.success) {
        toast.success(response.data.message || 'Picking force-completed! PP staff assigned.');
      } else {
        // Group was CANCELLED or not found — show guidance
        toast.warning(response.data.message);
        if (response.data.released_bundles > 0) {
          toast.info(`Released ${response.data.released_bundles} stuck bundle(s) to STORED.`);
        }
        if (response.data.action_needed === 'retry_pick_slips') {
          toast.info('Click "Retry Pick Slips" to create new pick slips for this session.');
        }
      }
      fetchSessions();
    } catch (error) {
      console.error('Force complete error:', error);
      toast.error(error.response?.data?.detail || 'Failed to force-complete picking');
    }
  };

  const openAssemblyDialog = (session) => {
    setActiveSession(session);
    setQuantityAssembled(1);
    setAssemblyDialogOpen(true);
  };

  const handleRecordAssembled = async (overrideSessionId) => {
    if (quantityAssembled < 1) {
      toast.error('Quantity must be at least 1');
      return;
    }
    
    setRecording(true);
    try {
      const sessionId = overrideSessionId || activeSession?.session_id;
      if (!sessionId) {
        toast.error('No session selected');
        return;
      }
      const response = await apiClient.post('/combo/assembly/record-assembled', {
        session_id: sessionId,
        quantity_assembled: quantityAssembled
      });
      
      toast.success(`Recorded ${quantityAssembled} assembled combos. ${response.data.remaining} remaining.`);
      
      if (response.data.session_status === 'COMPLETED') {
        toast.success('Assembly session completed!');

        // If the session spans multiple bundles with leftover > 0, open the mandatory
        // leftover-declaration modal BEFORE doing anything else. PP staff must tell the
        // system which bundle has the remaining pcs so downstream sessions are routed
        // to the correct bin.
        try {
          const leftoverResp = await apiClient.get(`/combo/assembly/sessions/${sessionId}/leftover-form`);
          const needs = (leftoverResp.data?.components || []).some(c => c.requires_declaration);
          if (needs && !leftoverResp.data?.leftover_declared) {
            setLeftoverForm(leftoverResp.data);
            setLeftoverSessionId(sessionId);
            // Seed inputs with the suggested FIFO attribution
            const seed = {};
            (leftoverResp.data.components || []).forEach(comp => {
              comp.bundles.forEach(b => { seed[b.bundle_code] = b.suggested_leftover || 0; });
            });
            setLeftoverInputs(seed);
            setAssemblyDialogOpen(false);
            fetchStats();
            fetchSessions();
            return; // don't trigger handover suggestions yet — do that after declaration
          }
        } catch (err) {
          console.error('Leftover form error:', err);
        }

        // Check for handover suggestions
        try {
          const hoResp = await apiClient.get(`/combo/assembly/sessions/${sessionId}/handover-suggestions`);
          if (hoResp.data.has_suggestions) {
            setHandoverSuggestions(hoResp.data.suggestions);
            setHandoverSourceSessionId(sessionId);
            setShowHandoverDialog(true);
          }
        } catch (err) {
          console.error('Handover suggestions error:', err);
        }
      }
      
      setAssemblyDialogOpen(false);
      fetchStats();
      fetchSessions();
    } catch (error) {
      console.error('Record assembled error:', error);
      toast.error(error.response?.data?.detail || 'Failed to record');
    } finally {
      setRecording(false);
    }
  };

  const handleCompletePartial = async (session) => {
    if (!window.confirm(`Complete this session with ${session.assembled_quantity} out of ${session.target_quantity} assembled? This cannot be undone.`)) return;
    try {
      const response = await apiClient.post(`/combo/assembly/complete-partial?session_id=${session.session_id}`);
      toast.success(response.data.message);
      fetchSessions();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete session');
    }
  };

  const handleHandoverBundle = async (suggestion, bundleCode) => {
    setHandingOver(bundleCode);
    try {
      const resp = await apiClient.post(`/combo/assembly/sessions/${handoverSourceSessionId}/handover-bundle`, {
        bundle_code: bundleCode,
        target_session_id: suggestion.target_session_id,
        component_barcode: suggestion.component_barcode
      });
      toast.success(resp.data.message);
      // Remove handed-over suggestion
      setHandoverSuggestions(prev => prev.filter(s =>
        !(s.target_session_id === suggestion.target_session_id && s.component_barcode === suggestion.component_barcode)
      ));
      fetchSessions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to hand over bundle');
    } finally {
      setHandingOver(null);
    }
  };

  // Submit PP staff's per-bundle leftover declaration (mandatory after multi-bundle session)
  const handleSubmitLeftover = async () => {
    if (!leftoverForm || !leftoverSessionId) return;

    // In manual mode, validate ALL components (not only requires_declaration ones)
    const componentsToValidate = manualLeftoverMode
      ? (leftoverForm.components || [])
      : (leftoverForm.components || []).filter(c => c.requires_declaration);

    // Validate each component's sum first
    for (const comp of componentsToValidate) {
      // In manual mode, components with expected_leftover=0 can be skipped (no declaration needed)
      if (manualLeftoverMode && comp.expected_leftover === 0) continue;
      if (!manualLeftoverMode && !comp.requires_declaration) continue;
      const sumForComp = (comp.bundles || []).reduce(
        (acc, b) => acc + (parseInt(leftoverInputs[b.bundle_code], 10) || 0), 0
      );
      if (sumForComp !== comp.expected_leftover) {
        toast.error(`${comp.component_name}: leftover must sum to ${comp.expected_leftover} pcs (you entered ${sumForComp})`);
        return;
      }
      // Per-bundle cap
      for (const b of (comp.bundles || [])) {
        const v = parseInt(leftoverInputs[b.bundle_code], 10) || 0;
        if (v < 0 || v > b.original_qty) {
          toast.error(`${b.bundle_code}: leftover must be between 0 and ${b.original_qty}`);
          return;
        }
      }
    }

    const leftovers = Object.entries(leftoverInputs)
      .map(([bundle_code, leftover_qty]) => ({ bundle_code, leftover_qty: parseInt(leftover_qty, 10) || 0 }))
      .filter(e => e.leftover_qty > 0);

    setSubmittingLeftover(true);
    try {
      const resp = await apiClient.post('/combo/assembly/declare-leftover', {
        session_id: leftoverSessionId,
        leftovers
      });
      toast.success(resp.data.message || 'Leftover recorded');

      if ((resp.data.swaps_applied || []).length > 0) {
        toast.info(`${resp.data.swaps_applied.length} downstream pick slip(s) reallocated`, { duration: 6000 });
      }
      if ((resp.data.swaps_failed || []).length > 0) {
        toast.warning(`${resp.data.swaps_failed.length} slip(s) need supervisor replanning`, { duration: 8000 });
      }

      const sid = leftoverSessionId;
      setLeftoverForm(null);
      setLeftoverSessionId(null);
      setLeftoverInputs({});
      setManualLeftoverMode(false);
      fetchSessions();
      fetchStats();

      // Continue with handover suggestions now that declaration is done
      try {
        const hoResp = await apiClient.get(`/combo/assembly/sessions/${sid}/handover-suggestions`);
        if (hoResp.data.has_suggestions) {
          setHandoverSuggestions(hoResp.data.suggestions);
          setHandoverSourceSessionId(sid);
          setShowHandoverDialog(true);
        }
      } catch (err) {
        console.error('Handover suggestions error:', err);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit leftover');
    } finally {
      setSubmittingLeftover(false);
    }
  };

  // Manually open the leftover-declaration modal (available for any session with picked
  // bundles, independent of the automatic trigger on session completion). Used when the
  // PP staff wants to record which bundle has the excess pieces even for a single-bundle
  // session, a partially-completed session, or a session where the auto prompt was missed.
  const handleOpenLeftoverManually = async (sessionId) => {
    if (!sessionId) return;
    setLoadingLeftoverForm(true);
    try {
      const leftoverResp = await apiClient.get(`/combo/assembly/sessions/${sessionId}/leftover-form`);
      const data = leftoverResp.data || {};
      const hasComponents = (data.components || []).length > 0;
      if (!hasComponents) {
        toast.info('No picked components yet — nothing to declare.');
        return;
      }
      setLeftoverForm(data);
      setLeftoverSessionId(sessionId);
      setManualLeftoverMode(true);
      const seed = {};
      (data.components || []).forEach(comp => {
        (comp.bundles || []).forEach(b => { seed[b.bundle_code] = b.suggested_leftover || 0; });
      });
      setLeftoverInputs(seed);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load leftover form');
    } finally {
      setLoadingLeftoverForm(false);
    }
  };



  const handlePrintStickers = async (session, isReprint = false) => {
    // Use USB printing flow
    await handleUsbPrintStickers(session, isReprint);
  };

  const handleReturnExcess = async (sessionId) => {
    // Open the scanning dialog instead of auto-FIFO
    setExcessScanSession(sessionId);
    setScannedReturnUsns({});
    setExcessScanInput('');
    setLoadingExcessData(true);
    setExcessScanDialog(true);
    
    try {
      const response = await apiClient.get(`/combo/assembly/session/${sessionId}/picked-usns`);
      setExcessComponents(response.data.components || []);
      // Initialize empty sets for each component
      const initial = {};
      for (const comp of (response.data.components || [])) {
        initial[comp.component_barcode] = new Set();
      }
      setScannedReturnUsns(initial);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load picked USNs');
      setExcessScanDialog(false);
    } finally {
      setLoadingExcessData(false);
    }
  };

  const handleExcessScanUSN = (usnCode) => {
    if (!usnCode.trim()) return;
    const trimmed = usnCode.trim();
    
    // Find which component this USN belongs to
    let found = false;
    for (const comp of excessComponents) {
      const usnEntry = comp.usns.find(u => u.usn_code === trimmed);
      if (usnEntry) {
        setScannedReturnUsns(prev => {
          const updated = { ...prev };
          const newSet = new Set(prev[comp.component_barcode] || []);
          if (newSet.has(trimmed)) {
            toast.info(`USN ${trimmed} already scanned`);
          } else {
            newSet.add(trimmed);
            toast.success(`Scanned: ${trimmed} (${comp.component_name})`);
          }
          updated[comp.component_barcode] = newSet;
          return updated;
        });
        found = true;
        break;
      }
    }
    
    if (!found) {
      toast.error(`USN ${trimmed} not found in any picked component bundle`);
    }
    
    setExcessScanInput('');
  };

  const handleConfirmExcessReturn = async () => {
    // Convert Sets to arrays for the API
    const returnedUsns = {};
    let totalReturning = 0;
    for (const [barcode, usnSet] of Object.entries(scannedReturnUsns)) {
      const arr = Array.from(usnSet);
      if (arr.length > 0) {
        returnedUsns[barcode] = arr;
        totalReturning += arr.length;
      }
    }
    
    if (totalReturning === 0) {
      if (!window.confirm('No USNs scanned for return. This means ALL picked USNs will be consumed for combos.\n\nProceed?')) {
        return;
      }
    }
    
    setReturningExcess(true);
    try {
      const response = await apiClient.post('/combo/assembly/return-excess-to-sfg', {
        session_id: excessScanSession,
        returned_usns: returnedUsns
      });
      const data = response.data;
      toast.success(data.message, { duration: 8000 });
      
      if (data.new_bundles?.length > 0) {
        // Persist the result so user can view bundle numbers anytime
        setExcessReturnResult({
          message: data.message,
          new_bundles: data.new_bundles,
          session_id: excessScanSession,
          timestamp: new Date().toLocaleTimeString('en-IN')
        });
      }
      
      setExcessScanDialog(false);
      fetchSessions();
      fetchStats();
    } catch (error) {
      console.error('Return excess error:', error);
      toast.error(error.response?.data?.detail || 'Failed to return excess');
    } finally {
      setReturningExcess(false);
    }
  };

  const selectSession = async (sessionId) => {
    if (selectedSessionId === sessionId) return;
    setSelectedSessionId(sessionId);
    try {
      const response = await apiClient.get(`/combo/assembly/sessions/${sessionId}`);
      setSessionDetail(response.data);
    } catch (error) {
      console.error('Error fetching session detail:', error);
      toast.error('Failed to fetch session details');
    }
  };

  // Keep toggleSessionDetail for backward compat
  const toggleSessionDetail = selectSession;

  const getStatusColor = (status) => {
    switch (status) {
      case 'PENDING_COMPONENTS': return 'bg-yellow-100 text-yellow-800';
      case 'ASSEMBLY_IN_PROGRESS': return 'bg-blue-100 text-blue-800';
      case 'COMPLETED': return 'bg-green-100 text-green-800';
      case 'CANCELLED': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const stepLabels = ['', 'Component Picking', 'Record Assembly', 'Print Stickers', 'Return Excess', 'Complete'];

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      {/* Header */}
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center">
            <Button
              variant="ghost"
              onClick={() => navigate('/wms/fg')}
              className="text-[#F9FAFB] hover:bg-white/10 mr-4"
              data-testid="combo-assembly-back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <Package className="w-8 h-8" />
              <div>
                <h1 className="text-xl font-bold">Combo Assembly Station</h1>
                <p className="text-sm text-[#E8DCC8]">Assemble combo products</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <PrintMethodToggle screenKey="combo-assembly" dark />
            {usbDevice ? (
              <Badge variant="outline" className="border-green-400 text-green-400">
                <Usb className="w-3 h-3 mr-1" />
                {usbDevice.productName || 'Connected'}
              </Badge>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={connectUSBPrinter}
                className="border-yellow-400 text-yellow-400 hover:bg-yellow-400/10"
                data-testid="connect-printer-btn"
              >
                <Usb className="w-3 h-3 mr-1" />
                Connect Printer
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowPrinterSettings(true)}
              className="border-[#E8DCC8] text-[#E8DCC8] hover:bg-white/10"
              data-testid="printer-settings-btn"
            >
              <Settings className="w-4 h-4" />
            </Button>
            <Button variant="outline" onClick={() => { fetchStats(); fetchSessions(); }} className="text-gray-800">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button
              onClick={() => setCreateDialogOpen(true)}
              className="bg-green-600 hover:bg-green-700"
              data-testid="combo-assembly-new-session-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Session
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        {/* Tabs for Manual vs Smart Planning */}
        <Tabs defaultValue="manual" className="w-full">
          <TabsList className="grid w-full max-w-md grid-cols-2 mb-6">
            <TabsTrigger value="manual" className="flex items-center gap-2" data-testid="manual-tab">
              <Boxes className="w-4 h-4" />
              Manual Session
            </TabsTrigger>
            <TabsTrigger value="smart" className="flex items-center gap-2" data-testid="smart-tab">
              <Calculator className="w-4 h-4" />
              Smart Planning
            </TabsTrigger>
          </TabsList>
          
          {/* Manual Session Tab */}
          <TabsContent value="manual">
            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-gray-500">Active Sessions</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold">{stats.active_sessions}</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-gray-500">Pending Slips</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold text-yellow-600">{stats.pending_component_slips}</p>
                </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-500">Pending Labels</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-orange-600">{stats.pending_labels}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-500">Pending Put Away</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-blue-600">{stats.pending_put_away}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-500">In Storage</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-green-600">{stats.combos_in_storage}</p>
            </CardContent>
          </Card>
          <Card className={(stats.floor_stock_bundles || 0) + (stats.unreturned_excess_sessions || 0) > 0 ? 'border-amber-300 bg-amber-50' : ''}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-500">Floor Stock</CardTitle>
            </CardHeader>
            <CardContent>
              <p className={`text-2xl font-bold ${(stats.floor_stock_bundles || 0) + (stats.unreturned_excess_sessions || 0) > 0 ? 'text-amber-600' : 'text-gray-400'}`}>
                {(stats.floor_stock_bundles || 0) + (stats.unreturned_excess_sessions || 0)}
              </p>
              {(stats.floor_stock_bundles || 0) + (stats.unreturned_excess_sessions || 0) > 0 && (
                <p className="text-xs text-amber-500 mt-1">
                  {stats.floor_stock_bundles > 0 ? `${stats.floor_stock_bundles} put-away` : ''}
                  {stats.floor_stock_bundles > 0 && stats.unreturned_excess_sessions > 0 ? ' + ' : ''}
                  {stats.unreturned_excess_sessions > 0 ? `${stats.unreturned_excess_sessions} unreturned` : ''}
                </p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sessions List */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Panel: Session List */}
          <div className="lg:col-span-1">
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between gap-2">
                  <CardTitle className="text-base flex-shrink-0">
                    {showExcessOverview ? 'Excess Pieces' : showFloorStock ? 'Floor Stock' : showHistory ? 'History' : 'Live Sessions'}
                  </CardTitle>
                  <div className="flex gap-1 flex-wrap justify-end">
                    {(showExcessOverview || showFloorStock) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setShowFloorStock(false);
                          setShowExcessOverview(false);
                          setSelectedSessionId(null); setSessionDetail(null);
                        }}
                        className="text-xs"
                        data-testid="back-to-live-btn"
                      >
                        <ArrowLeft className="w-3 h-3 mr-1" />
                        Live ({liveSessions.length})
                      </Button>
                    )}
                    {!showExcessOverview && !showFloorStock && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => { setShowHistory(!showHistory); setSelectedSessionId(null); setSessionDetail(null); }}
                        className="text-xs"
                        data-testid="toggle-history-btn"
                      >
                        <Clock className="w-3 h-3 mr-1" />
                        {showHistory ? `Live (${liveSessions.length})` : `History (${historySessions.length})`}
                      </Button>
                    )}
                    <Button
                      variant={showFloorStock ? 'default' : 'ghost'}
                      size="sm"
                      onClick={() => {
                        setShowFloorStock(!showFloorStock);
                        setShowExcessOverview(false);
                        if (!showFloorStock) fetchFloorStockStatus();
                        setSelectedSessionId(null); setSessionDetail(null);
                      }}
                      className={`text-xs ${showFloorStock ? 'bg-purple-600 hover:bg-purple-700 text-white' : ''}`}
                      data-testid="toggle-floor-stock-btn"
                    >
                      <Warehouse className="w-3 h-3 mr-1" />
                      Floor{floorStockCount > 0 ? ` (${floorStockCount})` : ''}
                    </Button>
                    <Button
                      variant={showExcessOverview ? 'default' : 'ghost'}
                      size="sm"
                      onClick={() => {
                        setShowExcessOverview(!showExcessOverview);
                        setShowFloorStock(false);
                        if (!showExcessOverview) fetchExcessOverview();
                        setSelectedSessionId(null); setSessionDetail(null);
                      }}
                      className={`text-xs ${showExcessOverview ? 'bg-amber-600 hover:bg-amber-700 text-white' : ''}`}
                      data-testid="toggle-excess-overview-btn"
                    >
                      <Package className="w-3 h-3 mr-1" />
                      Excess
                    </Button>
                  </div>
                </div>
                {/* Date filter — applies to Live & History tabs */}
                {!showFloorStock && !showExcessOverview && (
                  <div className="flex items-center gap-2 mt-3 pt-2 border-t" data-testid="sessions-date-filter-row">
                    <label className="text-xs text-gray-500 font-medium shrink-0">
                      Date:
                    </label>
                    <Input
                      type="date"
                      value={filterDate}
                      onChange={(e) => setFilterDate(e.target.value)}
                      className="h-8 text-xs w-[150px]"
                      data-testid="sessions-date-filter"
                    />
                    {filterDate && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2 text-xs text-gray-500 hover:text-gray-700"
                        onClick={() => {
                          const d = new Date();
                          const y = d.getFullYear();
                          const m = String(d.getMonth() + 1).padStart(2, '0');
                          const day = String(d.getDate()).padStart(2, '0');
                          setFilterDate(`${y}-${m}-${day}`);
                        }}
                        data-testid="sessions-date-filter-today-btn"
                      >
                        Today
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-xs text-gray-500 hover:text-gray-700"
                      onClick={() => setFilterDate('')}
                      data-testid="sessions-date-filter-all-btn"
                    >
                      All Dates
                    </Button>
                  </div>
                )}
              </CardHeader>
              <CardContent className="p-2">
                {/* Floor Stock Overview */}
                {showFloorStock ? (
                  <div className="space-y-2 max-h-[calc(100vh-280px)] overflow-y-auto px-1">
                    {loadingFloorStock ? (
                      <p className="text-center py-6 text-gray-400 text-sm">Loading floor stock...</p>
                    ) : !floorStockData || floorStockData.total_bundles === 0 ? (
                      <div className="text-center py-6">
                        <Warehouse className="w-10 h-10 mx-auto text-gray-300 mb-2" />
                        <p className="text-gray-400 text-sm">No stranded floor stock</p>
                        <p className="text-xs text-gray-300 mt-1">All combo return bundles have been put away or released</p>
                      </div>
                    ) : (
                      <>
                        {/* Summary header */}
                        <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mb-2">
                          <div className="flex items-center justify-between mb-2">
                            <div>
                              <p className="text-sm font-semibold text-purple-900">Floor Stock Summary</p>
                              <p className="text-xs text-purple-600">{floorStockData.total_bundles} bundle(s) / {floorStockData.total_pieces} excess pcs</p>
                            </div>
                            {floorStockData.pending_putaway && floorStockData.pending_putaway.bundles > 0 && (
                              <Button
                                size="sm"
                                onClick={handleReleaseFloorStock}
                                disabled={releasingFloorStock}
                                className="bg-purple-600 hover:bg-purple-700 text-white"
                                data-testid="release-floor-stock-btn"
                              >
                                {releasingFloorStock ? <RefreshCw className="w-3 h-3 mr-1 animate-spin" /> : <RotateCcw className="w-3 h-3 mr-1" />}
                                Release Put-Away
                              </Button>
                            )}
                          </div>
                          <div className="flex gap-3 text-xs">
                            {floorStockData.pending_putaway && floorStockData.pending_putaway.bundles > 0 && (
                              <span className="text-purple-500">Pending put-away: {floorStockData.pending_putaway.bundles} bundles</span>
                            )}
                            {floorStockData.unreturned_excess && floorStockData.unreturned_excess.sessions_count > 0 && (
                              <span className="text-amber-600">Unreturned excess: {floorStockData.unreturned_excess.sessions_count} sessions ({floorStockData.unreturned_excess.pieces} pcs)</span>
                            )}
                          </div>
                        </div>

                        {/* Session list */}
                        {floorStockData.sessions.map((session) => (
                          <div key={session.session_id} className={`border rounded-lg p-3 bg-white ${session.category === 'unreturned_excess' ? 'border-amber-200' : ''}`} data-testid={`floor-stock-session-${session.session_id}`}>
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <p className="text-xs font-mono text-gray-400 truncate">{session.session_id}</p>
                                {session.category === 'unreturned_excess' && (
                                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-medium">Unreturned</span>
                                )}
                                {session.category === 'pending_putaway' && (
                                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 font-medium">Put-Away</span>
                                )}
                              </div>
                              <span className="text-xs font-bold text-purple-700">{session.total_pieces} pcs</span>
                            </div>
                            {session.combo_name && (
                              <p className="text-xs text-gray-500 mb-1">{session.combo_name} {session.shade ? `| ${session.shade}` : ''} {session.size ? `| ${session.size}` : ''}</p>
                            )}
                            {session.bundles.map((b) => (
                              <div key={b.bundle_code} className="flex items-center justify-between text-xs bg-gray-50 rounded px-2 py-1 mt-1">
                                <div>
                                  <span className="font-mono">{b.bundle_code}</span>
                                  <span className="text-gray-400 ml-2">{b.design_name || b.component_name || ''}</span>
                                </div>
                                <div className="text-right">
                                  {b.excess_pieces ? (
                                    <span className="text-amber-600 font-medium">{b.excess_pieces} excess pcs</span>
                                  ) : (
                                    <span className="text-gray-500">{b.usn_count} pcs</span>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        ))}
                      </>
                    )}
                  </div>
                ) : (
                <>
                {/* Excess Pieces Overview */}
                {showExcessOverview ? (
                  <div className="space-y-2 max-h-[calc(100vh-280px)] overflow-y-auto px-1">
                    {loadingExcessOverview ? (
                      <p className="text-center py-6 text-gray-400 text-sm">Loading excess data...</p>
                    ) : excessOverviewData.length === 0 ? (
                      <div className="text-center py-6">
                        <p className="text-gray-400 text-sm">No sessions with excess pieces found</p>
                      </div>
                    ) : (
                      excessOverviewData.map((session) => (
                        <div key={session.session_id} className="border rounded-lg p-3 bg-amber-50 border-amber-200" data-testid={`excess-session-${session.session_id}`}>
                          <div className="flex items-center justify-between mb-2">
                            <div>
                              <p className="font-semibold text-sm">{session.design_name}</p>
                              <p className="text-xs text-gray-500">{session.shade} | {session.size}</p>
                            </div>
                            <Badge variant="outline" className="text-xs bg-amber-100 border-amber-300 text-amber-800">
                              {session.assembled_quantity}/{session.target_quantity} assembled
                            </Badge>
                          </div>
                          {session.excess_components.map((comp, idx) => (
                            <div key={idx} className="mt-2 bg-white rounded p-2 border border-gray-100">
                              <div className="flex items-center justify-between mb-1">
                                <p className="text-xs font-medium">{comp.component_name}</p>
                                <span className="text-xs font-bold text-amber-700">{comp.excess_pieces} excess pcs</span>
                              </div>
                              <p className="text-xs text-gray-400 mb-1">Picked: {comp.total_picked} | Used: {comp.consumed}</p>
                              <div className="space-y-1">
                                {comp.bundles.map((b) => (
                                  <div key={b.bundle_code} className="flex items-center justify-between text-xs bg-gray-50 rounded px-2 py-1">
                                    <span className="font-mono">{b.bundle_code}</span>
                                    <span className="text-gray-500">{b.usn_count} pcs | {b.bin_location || 'No bin'}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      ))
                    )}
                  </div>
                ) : (
                <>
                {/* Search Bar */}
                <div className="px-1 pb-2">
                  <Input
                    placeholder="Search design, shade, size, staff..."
                    value={sessionSearch}
                    onChange={(e) => setSessionSearch(e.target.value)}
                    className="h-8 text-xs"
                    data-testid="session-search-input"
                  />
                </div>
                {loadingSessions ? (
                  <p className="text-center py-6 text-gray-400 text-sm">Loading...</p>
                ) : filteredDisplaySessions.length === 0 ? (
                  <div className="text-center py-6">
                    <p className="text-gray-400 text-sm">{sessionSearch ? 'No matches found' : showHistory ? 'No completed sessions' : 'No active sessions'}</p>
                  </div>
                ) : (
                  <div className="space-y-1 max-h-[60vh] overflow-y-auto">
                    {filteredDisplaySessions.map((session) => {
                      const step = getWizardStep(session);
                      const isSelected = selectedSessionId === session.session_id;
                      return (
                        <div
                          key={session.session_id}
                          className={`p-3 rounded-lg cursor-pointer transition-all ${
                            isSelected 
                              ? 'bg-[#2D5F4F] text-white shadow-md' 
                              : 'bg-white hover:bg-[#F5F1E8] border border-[#E5DFD0]'
                          }`}
                          onClick={() => selectSession(session.session_id)}
                          data-testid={`session-card-${session.session_id}`}
                        >
                          <p className={`font-medium text-sm truncate ${isSelected ? 'text-white' : 'text-[#1A1F2E]'}`}>
                            {session.design_name || session.combo_name}
                          </p>
                          <p className={`text-xs truncate ${isSelected ? 'text-green-100' : 'text-gray-400'}`}>
                            {[session.shade, session.size, session.description].filter(Boolean).join(' / ') || session.combo_barcode}
                          </p>
                          <div className="flex items-center justify-between mt-1">
                            <span className={`text-xs ${isSelected ? 'text-green-200' : 'text-gray-500'}`}>
                              {session.assembled_quantity}/{session.target_quantity} assembled
                            </span>
                            {!showHistory && session.status !== 'CANCELLED' && (
                              <span className={`text-xs px-2 py-0.5 rounded-full ${
                                step === 1 ? 'bg-yellow-100 text-yellow-700' :
                                step === 2 ? 'bg-blue-100 text-blue-700' :
                                step === 3 ? 'bg-purple-100 text-purple-700' :
                                'bg-amber-100 text-amber-700'
                              }`}>
                                Step {step}
                              </span>
                            )}
                            {session.status === 'CANCELLED' && (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700">
                                Cancelled
                              </span>
                            )}
                          </div>
                          <p className={`text-[10px] mt-0.5 font-mono ${isSelected ? 'text-green-300/60' : 'text-gray-300'}`}>
                            {session.session_id}
                          </p>
                          {session.pp_staff_name && (
                            <p className={`text-xs mt-1 truncate ${isSelected ? 'text-green-200' : 'text-gray-500'}`} data-testid={`session-staff-${session.session_id}`}>
                              Staff: {session.pp_staff_name}
                            </p>
                          )}
                          {/* Component Picking Progress */}
                          {session.component_progress && session.component_progress.length > 0 && !showHistory && (
                            <div className="mt-2 space-y-1" data-testid={`component-progress-${session.session_id}`}>
                              {session.component_progress.map((comp) => {
                                const pct = comp.total_bundles > 0 ? Math.round((comp.picked_bundles / comp.total_bundles) * 100) : 0;
                                return (
                                  <div key={comp.component_barcode} className="flex items-center gap-1.5">
                                    <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                                      comp.status === 'COMPLETED' ? 'bg-green-500' :
                                      comp.status === 'IN_PROGRESS' ? 'bg-blue-500' :
                                      comp.status === 'NO_SLIP' ? 'bg-gray-300' : 'bg-yellow-400'
                                    }`} />
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center justify-between">
                                        <span className={`text-[10px] truncate ${isSelected ? 'text-green-200' : 'text-gray-500'}`}>
                                          {comp.component_name || comp.component_barcode}
                                        </span>
                                        <span className={`text-[10px] ml-1 flex-shrink-0 ${isSelected ? 'text-green-300' : 'text-gray-400'}`}>
                                          {comp.picked_bundles}/{comp.total_bundles}
                                        </span>
                                      </div>
                                      <div className={`w-full h-0.5 rounded-full ${isSelected ? 'bg-green-900/40' : 'bg-gray-200'}`}>
                                        <div className={`h-0.5 rounded-full transition-all ${
                                          comp.status === 'COMPLETED' ? 'bg-green-500' :
                                          comp.status === 'IN_PROGRESS' ? 'bg-blue-500' : 'bg-yellow-400'
                                        }`} style={{ width: `${pct}%` }} />
                                      </div>
                                    </div>
                                    {comp.picker_name && (
                                      <span className={`text-[9px] flex-shrink-0 ${isSelected ? 'text-green-300/60' : 'text-gray-300'}`}>
                                        {comp.picker_name}
                                      </span>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
                </>
                )}
                </>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right Panel: Guided Wizard */}
          <div className="lg:col-span-2">
            {!selectedSession ? (
              <Card className="h-full flex items-center justify-center">
                <CardContent className="text-center py-16">
                  <Package className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-400">Select a session to begin</p>
                  <p className="text-sm text-gray-300 mt-1">Or create a new session</p>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-lg">{selectedSession.combo_name}</CardTitle>
                      <CardDescription className="font-mono text-xs">{selectedSession.combo_barcode} | {selectedSession.design_name || '-'} / {selectedSession.shade || '-'} / {selectedSession.size || '-'}{selectedSession.description ? ` / ${selectedSession.description}` : ''}</CardDescription>
                      <p className="text-[10px] font-mono text-gray-300 mt-0.5">{selectedSession.session_id}</p>
                      {(selectedSession.pp_staff_name || selectedSession.started_by_name || selectedSession.created_by_name) && (
                        <p className="text-xs text-gray-500 mt-1">
                          Staff: {selectedSession.pp_staff_name || selectedSession.started_by_name || selectedSession.created_by_name}
                          {(selectedSession.pp_staff_id || selectedSession.started_by_staff_id || selectedSession.created_by_staff_id) && (
                            <span className="text-gray-400 ml-1">({selectedSession.pp_staff_id || selectedSession.started_by_staff_id || selectedSession.created_by_staff_id})</span>
                          )}
                        </p>
                      )}
                    </div>
                    <div className="text-right space-y-1">
                      <p className="text-2xl font-bold text-[#1A1F2E]">{selectedSession.assembled_quantity}<span className="text-sm font-normal text-gray-400">/{selectedSession.target_quantity}</span></p>
                      <Badge className={getStatusColor(selectedSession.status)}>
                        {selectedSession.status?.replace(/_/g, ' ')}
                      </Badge>
                      {selectedSession.status !== 'COMPLETED' && selectedSession.status !== 'CANCELLED' && (
                        <div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setCancelSessionTarget(selectedSession);
                              setCancelDialogOpen(true);
                            }}
                            className="text-xs text-red-500 hover:text-red-700 hover:bg-red-50 px-2 py-1 h-auto"
                            data-testid="cancel-session-btn"
                          >
                            <XCircle className="w-3 h-3 mr-1" />
                            Cancel Session
                          </Button>
                        </div>
                      )}
                      {selectedSession.status === 'CANCELLED' && (
                        <p className="text-[10px] text-red-400 mt-1">
                          {selectedSession.cancel_reason && `Reason: ${selectedSession.cancel_reason}`}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Step Progress Bar */}
                  {!showHistory && (
                    <div className="flex items-center gap-1 mt-4" data-testid="wizard-progress">
                      {[1, 2, 3, 4].map((s) => (
                        <div key={s} className="flex-1 flex items-center gap-1">
                          <div className={`flex-1 h-2 rounded-full ${
                            s < wizardStep ? 'bg-green-500' : s === wizardStep ? 'bg-[#2D5F4F]' : 'bg-gray-200'
                          }`} />
                          <span className={`text-[10px] whitespace-nowrap ${
                            s === wizardStep ? 'font-bold text-[#2D5F4F]' : s < wizardStep ? 'text-green-600' : 'text-gray-400'
                          }`}>{stepLabels[s]}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </CardHeader>

                <CardContent className="space-y-4">
                  {/* ====== STEP 1: Component Picking Status ====== */}
                  <div className={`rounded-lg border ${wizardStep === 1 ? 'border-[#2D5F4F] bg-white' : 'border-gray-200 bg-gray-50'} p-4`}>
                    <div className="flex items-center gap-2 mb-3">
                      {wizardStep > 1 ? <Check className="w-5 h-5 text-green-500" /> : <span className="w-5 h-5 rounded-full bg-[#2D5F4F] text-white text-xs flex items-center justify-center font-bold">1</span>}
                      <p className={`font-medium text-sm ${wizardStep === 1 ? 'text-[#1A1F2E]' : 'text-gray-500'}`}>Component Picking</p>
                    </div>
                    
                    {sessionDetail && (
                      <div className="space-y-2">
                        {selectedSession.components?.map((comp, idx) => {
                          const compSlips = sessionDetail.wms_pick_slips?.filter(s => s.sku_code === comp.component_barcode) || [];
                          const pickedBundles = compSlips.flatMap(s => (s.bundles || []).filter(b => b.picked).map(b => ({ bundle_code: b.bundle_code, usn_count: b.usn_count || 0, slip_no: s.slip_no })));
                          const totalPicked = pickedBundles.reduce((sum, b) => sum + b.usn_count, 0);
                          const allPicked = compSlips.length > 0 && compSlips.every(s => s.status === 'COMPLETED' || s.status === 'COMPLETED_WITH_SKIPS');
                          const slipNos = [...new Set(compSlips.map(s => s.slip_no))];

                          return (
                            <div key={idx} className={`flex items-center justify-between p-2 rounded ${allPicked ? 'bg-green-50' : 'bg-yellow-50'}`} data-testid={`wizard-comp-${comp.component_barcode}`}>
                              <div className="flex items-center gap-2 flex-1 min-w-0">
                                {allPicked ? <Check className="w-4 h-4 text-green-600 flex-shrink-0" /> : <Clock className="w-4 h-4 text-yellow-600 flex-shrink-0" />}
                                <div className="min-w-0">
                                  <p className="text-sm font-medium">{comp.component_name || comp.component_barcode}</p>
                                  <p className="text-xs text-gray-500 truncate">
                                    {slipNos.length > 0 ? slipNos.join(', ') : 'No slip'} | {pickedBundles.map(b => b.bundle_code).join(', ') || 'Not picked'}
                                  </p>
                                  {compSlips.length > 0 && compSlips[0].assigned_to_name && (
                                    <p className="text-xs text-indigo-600 mt-0.5">
                                      Picker: {compSlips[0].assigned_to_name}
                                    </p>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-2 flex-shrink-0">
                                <div className="text-right">
                                  <p className={`text-sm font-bold ${allPicked ? 'text-green-700' : 'text-yellow-700'}`}>{totalPicked} pcs</p>
                                  <p className="text-xs text-gray-400">{comp.quantity} per combo</p>
                                </div>
                                {!allPicked && selectedSession.status !== 'CANCELLED' && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => openAssignPickerDialog(selectedSession.session_id, comp.component_barcode, comp.component_name || comp.component_barcode)}
                                    className="text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 h-7 w-7 p-0"
                                    title={compSlips[0]?.assigned_to_name ? 'Reassign picker' : 'Assign picker'}
                                    data-testid={`assign-comp-picker-${comp.component_barcode}`}
                                  >
                                    <UserPlus className="w-3.5 h-3.5" />
                                  </Button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {wizardStep === 1 && (
                      <div className="flex gap-2 mt-3 flex-wrap">
                        <Button size="sm" variant="outline" onClick={() => handleRetryPickSlips(selectedSession)} data-testid="wizard-retry-picks">
                          <RefreshCw className="w-4 h-4 mr-1" />Retry Pick Slips
                        </Button>
                        <Button size="sm" onClick={() => handleStartAssembly(selectedSession)} className="bg-[#2D5F4F] hover:bg-[#1A4A3A]" data-testid="wizard-start-assembly">
                          <Play className="w-4 h-4 mr-1" />Start Assembly
                        </Button>
                        <Button size="sm" variant="outline" className="border-orange-300 text-orange-700 hover:bg-orange-50" onClick={() => handleForceCompletePicking(selectedSession)} data-testid="wizard-force-complete">
                          <CheckCircle className="w-4 h-4 mr-1" />Force Complete Picking
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* ====== STEP 2: Record Assembly ====== */}
                  <div className={`rounded-lg border ${wizardStep === 2 ? 'border-[#2D5F4F] bg-white' : 'border-gray-200 bg-gray-50'} p-4 ${wizardStep < 2 ? 'opacity-50' : ''}`}>
                    <div className="flex items-center gap-2 mb-3">
                      {wizardStep > 2 ? <Check className="w-5 h-5 text-green-500" /> : <span className="w-5 h-5 rounded-full bg-[#2D5F4F] text-white text-xs flex items-center justify-center font-bold">2</span>}
                      <p className={`font-medium text-sm ${wizardStep === 2 ? 'text-[#1A1F2E]' : 'text-gray-500'}`}>Record Assembly</p>
                    </div>
                    
                    {wizardStep === 2 && (
                      <div className="space-y-3">
                        {/* Component availability warning */}
                        {sessionDetail?.max_assemblable !== undefined && sessionDetail.max_assemblable < (selectedSession.target_quantity - selectedSession.assembled_quantity) && (
                          <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 text-xs text-amber-800" data-testid="component-availability-warning">
                            <div className="flex items-center gap-1 mb-1">
                              <AlertTriangle className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
                              <span className="font-semibold">Component shortage — max {sessionDetail.max_assemblable} combo(s) possible</span>
                            </div>
                            {sessionDetail.component_availability?.filter(c => c.max_new_combos < (selectedSession.target_quantity - selectedSession.assembled_quantity)).map((c) => (
                              <p key={c.barcode} className="ml-5 text-amber-600">
                                {c.component}: {c.picked_pieces} pcs picked (max {c.max_new_combos} new)
                              </p>
                            ))}
                          </div>
                        )}
                        <div className="flex items-end gap-3">
                          <div className="flex-1">
                            <Label className="text-xs">Quantity Assembled</Label>
                            <Input
                              type="number"
                              min={1}
                              max={sessionDetail?.max_assemblable !== undefined ? Math.min(sessionDetail.max_assemblable, selectedSession.target_quantity - selectedSession.assembled_quantity) : selectedSession.target_quantity - selectedSession.assembled_quantity}
                              value={quantityAssembled}
                              onChange={(e) => setQuantityAssembled(parseInt(e.target.value) || 1)}
                              data-testid="wizard-qty-input"
                            />
                            {sessionDetail?.max_assemblable !== undefined && (
                              <p className="text-[10px] text-gray-400 mt-0.5">
                                Max: {Math.min(sessionDetail.max_assemblable, selectedSession.target_quantity - selectedSession.assembled_quantity)} based on picked components
                              </p>
                            )}
                          </div>
                          <Button
                            onClick={() => { setActiveSession(selectedSession); handleRecordAssembled(selectedSession.session_id); }}
                            className="bg-green-600 hover:bg-green-700"
                            disabled={recording || (sessionDetail?.max_assemblable !== undefined && sessionDetail.max_assemblable < 1)}
                            data-testid="wizard-record-btn"
                          >
                            {recording ? <><RefreshCw className="w-4 h-4 mr-1 animate-spin" />Recording...</> : <><Check className="w-4 h-4 mr-1" />Record</>}
                          </Button>
                        </div>
                        {selectedSession.assembled_quantity > 0 && selectedSession.assembled_quantity < selectedSession.target_quantity && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="mt-2 border-orange-300 text-orange-700 hover:bg-orange-50 w-full"
                            onClick={() => handleCompletePartial(selectedSession)}
                            data-testid="wizard-complete-partial-btn"
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />Complete Session ({selectedSession.assembled_quantity}/{selectedSession.target_quantity})
                          </Button>
                        )}
                        {/* Manual leftover declaration — available whenever components have been picked */}
                        <Button
                          size="sm"
                          variant="outline"
                          className="mt-2 border-indigo-300 text-indigo-700 hover:bg-indigo-50 w-full"
                          onClick={() => handleOpenLeftoverManually(selectedSession.session_id)}
                          disabled={loadingLeftoverForm}
                          data-testid="wizard-declare-leftover-btn"
                          title="Tell the system which bundle has the excess pieces"
                        >
                          {loadingLeftoverForm ? (
                            <><RefreshCw className="w-4 h-4 mr-1 animate-spin" />Loading...</>
                          ) : (
                            <>
                              <Package className="w-4 h-4 mr-1" />
                              Declare Bundle with Excess Pieces
                              {selectedSession.leftover_declared && <Check className="w-3.5 h-3.5 ml-1 text-green-600" />}
                            </>
                          )}
                        </Button>
                      </div>
                    )}
                    {wizardStep > 2 && (
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <p className="text-sm text-green-600">{selectedSession.assembled_quantity} combos recorded</p>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                          onClick={() => handleOpenLeftoverManually(selectedSession.session_id)}
                          disabled={loadingLeftoverForm}
                          data-testid="wizard-declare-leftover-btn-later"
                          title="Re-open the leftover declaration for this session"
                        >
                          {loadingLeftoverForm ? (
                            <><RefreshCw className="w-4 h-4 mr-1 animate-spin" />Loading...</>
                          ) : (
                            <>
                              <Package className="w-4 h-4 mr-1" />
                              {selectedSession.leftover_declared ? 'Re-declare Leftover' : 'Declare Leftover'}
                              {selectedSession.leftover_declared && <Check className="w-3.5 h-3.5 ml-1 text-green-600" />}
                            </>
                          )}
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* ====== STEP 3: Print Stickers ====== */}
                  <div className={`rounded-lg border ${wizardStep === 3 ? 'border-[#2D5F4F] bg-white' : 'border-gray-200 bg-gray-50'} p-4 ${wizardStep < 3 ? 'opacity-50' : ''}`}>
                    <div className="flex items-center gap-2 mb-3">
                      {wizardStep > 3 ? <Check className="w-5 h-5 text-green-500" /> : <span className="w-5 h-5 rounded-full bg-[#2D5F4F] text-white text-xs flex items-center justify-center font-bold">3</span>}
                      <p className={`font-medium text-sm ${wizardStep === 3 ? 'text-[#1A1F2E]' : 'text-gray-500'}`}>Print Stickers</p>
                    </div>
                    
                    {wizardStep >= 3 && (
                      <Button
                        size="sm"
                        onClick={() => handlePrintStickers(selectedSession, wizardStep > 3)}
                        disabled={printing}
                        className={wizardStep === 3 ? 'bg-[#2D5F4F] hover:bg-[#1A4A3A]' : ''}
                        variant={wizardStep > 3 ? 'outline' : 'default'}
                        data-testid="wizard-print-btn"
                      >
                        <Printer className="w-4 h-4 mr-1" />
                        {printing ? 'Printing...' : wizardStep > 3 ? 'Reprint Stickers' : 'Print Stickers'}
                      </Button>
                    )}
                  </div>

                  {/* ====== STEP 4: Return Excess to SFG ====== */}
                  {(() => {
                    const canReturnExcess = selectedSession.status === 'COMPLETED' && !selectedSession.excess_returned;
                    const dimmed = !canReturnExcess && wizardStep < 5;
                    return (
                  <div className={`rounded-lg border ${wizardStep === 4 ? 'border-[#2D5F4F] bg-white' : 'border-gray-200 bg-gray-50'} p-4 ${dimmed ? 'opacity-50' : ''}`}>
                    <div className="flex items-center gap-2 mb-3">
                      {wizardStep > 4 ? <Check className="w-5 h-5 text-green-500" /> : <span className="w-5 h-5 rounded-full bg-[#2D5F4F] text-white text-xs flex items-center justify-center font-bold">4</span>}
                      <p className={`font-medium text-sm ${wizardStep === 4 || canReturnExcess ? 'text-[#1A1F2E]' : 'text-gray-500'}`}>Return Excess to SFG</p>
                      {canReturnExcess && !selectedSession.labels_printed && (
                        <span className="text-[10px] text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded font-medium" data-testid="return-excess-available-hint">
                          Available — can be done before or after printing
                        </span>
                      )}
                      {selectedSession.partial_completion && (
                        <span className="text-[10px] text-indigo-700 bg-indigo-100 px-1.5 py-0.5 rounded font-medium" data-testid="partial-completion-badge">
                          Partial ({selectedSession.assembled_quantity}/{selectedSession.original_target || selectedSession.target_quantity})
                        </span>
                      )}
                    </div>

                    {canReturnExcess && (
                      <Button
                        size="sm"
                        onClick={() => handleReturnExcess(selectedSession.session_id)}
                        disabled={returningExcess}
                        className="bg-amber-600 hover:bg-amber-700"
                        data-testid="wizard-return-excess-btn"
                      >
                        <RotateCcw className="w-4 h-4 mr-1" />
                        {returningExcess ? 'Processing...' : 'Scan & Return Excess USNs'}
                      </Button>
                    )}
                    {wizardStep >= 5 && (
                      <div className="space-y-3" data-testid="excess-return-summary">
                        <Badge className="bg-green-100 text-green-700">Excess Returned</Badge>
                        
                        {/* Show return summary with new bundle numbers */}
                        {sessionDetail?.session?.return_summary?.length > 0 && (
                          <div className="mt-3 space-y-2">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Return Summary</p>
                            {sessionDetail.session.return_summary.map((item, idx) => (
                              <div key={idx} className="flex items-center justify-between border rounded-lg px-3 py-2 bg-white" data-testid={`return-summary-${idx}`}>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-[#1A1F2E] truncate">{item.component}</p>
                                  <p className="text-xs text-gray-500">
                                    {item.consumed} consumed, {item.excess} excess of {item.total_pieces} total
                                  </p>
                                </div>
                                {item.new_bundle_code && (
                                  <div className="flex items-center gap-2 shrink-0 ml-3">
                                    <div className="text-right">
                                      <p className="font-mono font-bold text-sm text-blue-700" data-testid={`new-bundle-code-${idx}`}>{item.new_bundle_code}</p>
                                      <p className="text-[10px] text-gray-400">New Bundle</p>
                                    </div>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Also show from excessReturnResult if just completed in this session */}
                        {!sessionDetail?.session?.return_summary?.length && excessReturnResult?.session_id === selectedSession?.session_id && excessReturnResult.new_bundles?.length > 0 && (
                          <div className="mt-3 space-y-2">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">New Bundles Created</p>
                            {excessReturnResult.new_bundles.map((nb, idx) => (
                              <div key={idx} className="flex items-center justify-between border rounded-lg px-3 py-2 bg-white" data-testid={`new-bundle-${nb.bundle_code}`}>
                                <div>
                                  <p className="text-sm font-medium text-[#1A1F2E]">{nb.component}</p>
                                  <p className="text-xs text-gray-500">{nb.usn_count} pcs</p>
                                </div>
                                <p className="font-mono font-bold text-sm text-blue-700">{nb.bundle_code}</p>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  );
                  })()}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
          </TabsContent>
          
          {/* Smart Planning Tab */}
          <TabsContent value="smart">
            <SmartComboPlanningTab />
          </TabsContent>
        </Tabs>
      </main>

      {/* Create Session Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={handleDialogOpenChange}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>New Assembly Session</DialogTitle>
            <DialogDescription>
              Select combo product using cascading filters. This will generate component pick slips.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Design Name Dropdown */}
            <div>
              <Label>Design Name *</Label>
              <Select value={selectedDesignName} onValueChange={handleDesignNameChange}>
                <SelectTrigger data-testid="design-name-select">
                  <SelectValue placeholder="Select design name" />
                </SelectTrigger>
                <SelectContent>
                  {designNames.map((name) => (
                    <SelectItem key={name} value={name}>
                      {name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Shade Dropdown */}
            <div>
              <Label>Shade *</Label>
              <Select 
                value={selectedShade} 
                onValueChange={handleShadeChange}
                disabled={!selectedDesignName || shades.length === 0}
              >
                <SelectTrigger data-testid="shade-select">
                  <SelectValue placeholder={selectedDesignName ? "Select shade" : "Select design first"} />
                </SelectTrigger>
                <SelectContent>
                  {shades.map((shade) => (
                    <SelectItem key={shade} value={shade}>
                      {shade}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Size Dropdown */}
            <div>
              <Label>Size *</Label>
              <Select 
                value={selectedSize} 
                onValueChange={handleSizeChange}
                disabled={!selectedShade || sizes.length === 0}
              >
                <SelectTrigger data-testid="size-select">
                  <SelectValue placeholder={selectedShade ? "Select size" : "Select shade first"} />
                </SelectTrigger>
                <SelectContent>
                  {sizes.map((size) => (
                    <SelectItem key={size} value={size}>
                      {size}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Description Dropdown */}
            <div>
              <Label>Description *</Label>
              <Select 
                value={selectedDescription} 
                onValueChange={handleDescriptionChange}
                disabled={!selectedSize || descriptions.length === 0}
              >
                <SelectTrigger data-testid="description-select">
                  <SelectValue placeholder={selectedSize ? "Select description" : "Select size first"} />
                </SelectTrigger>
                <SelectContent>
                  {descriptions.map((desc) => (
                    <SelectItem key={desc} value={desc}>
                      {desc}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Auto-detected Combo Barcode */}
            {detectedCombo && (
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Check className="w-4 h-4 text-green-600" />
                  <span className="font-medium text-green-800">Combo Detected</span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-gray-500">Barcode:</span>
                    <span className="ml-2 font-mono font-medium">{detectedCombo.combo_barcode}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Name:</span>
                    <span className="ml-2 font-medium">{detectedCombo.combo_name}</span>
                  </div>
                </div>
                {detectedCombo.components && detectedCombo.components.length > 0 && (
                  <div className="mt-2 text-xs text-gray-600">
                    Components: {detectedCombo.components.map(c => c.component_name).join(', ')}
                  </div>
                )}
              </div>
            )}

            {loadingDropdowns && (
              <div className="flex items-center justify-center py-2">
                <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                <span className="text-sm text-gray-500">Loading options...</span>
              </div>
            )}
            
            <div>
              <Label>Target Quantity *</Label>
              <Input
                type="number"
                min="1"
                value={targetQuantity}
                onChange={(e) => setTargetQuantity(parseInt(e.target.value) || 1)}
                data-testid="target-quantity-input"
              />
              <p className="text-xs text-gray-500 mt-1">
                How many combo units do you want to assemble?
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => handleDialogOpenChange(false)}>Cancel</Button>
            <Button onClick={handleCreateSession} disabled={creating || !selectedCombo}>
              {creating ? 'Creating...' : 'Create Session'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Record Assembly Dialog */}
      <Dialog open={assemblyDialogOpen} onOpenChange={setAssemblyDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Record Assembled Combos</DialogTitle>
            <DialogDescription>
              Enter the quantity of combo units you have assembled.
            </DialogDescription>
          </DialogHeader>
          
          {activeSession && (
            <div className="space-y-4 py-4">
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-sm text-gray-500">Session</p>
                <p className="font-medium">{activeSession.combo_name}</p>
                <p className="font-mono text-xs text-gray-400">{activeSession.session_id}</p>
              </div>
              
              <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                <div>
                  <p className="text-sm text-gray-600">Current Progress</p>
                  <p className="text-2xl font-bold">
                    {activeSession.assembled_quantity} / {activeSession.target_quantity}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-600">Remaining</p>
                  <p className="text-xl font-semibold text-orange-600">
                    {activeSession.target_quantity - activeSession.assembled_quantity}
                  </p>
                </div>
              </div>
              
              <div>
                <Label>Quantity Assembled *</Label>
                <Input
                  type="number"
                  min="1"
                  max={activeSession.target_quantity - activeSession.assembled_quantity}
                  value={quantityAssembled}
                  onChange={(e) => setQuantityAssembled(parseInt(e.target.value) || 1)}
                  className="text-lg font-semibold text-center"
                  data-testid="quantity-assembled-input"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Max: {activeSession.target_quantity - activeSession.assembled_quantity}
                </p>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssemblyDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleRecordAssembled} 
              disabled={recording || quantityAssembled < 1}
              className="bg-green-600 hover:bg-green-700"
              data-testid="record-assembled-submit"
            >
              {recording ? 'Recording...' : 'Record'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* USB Print Progress Dialog */}
      <Dialog open={printDialogOpen && printing} onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Printer className="w-5 h-5 text-amber-600" />
              Printing Combo Stickers
            </DialogTitle>
            <DialogDescription>
              {printDialogSession?.combo_name || 'Processing...'}
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center justify-center gap-3 py-8">
            <RefreshCw className="w-8 h-8 animate-spin text-amber-600" />
            <span className="text-lg font-medium text-amber-700">{printProgress}</span>
          </div>
        </DialogContent>
      </Dialog>

      {/* Box Sticker Alert Dialog */}
      <Dialog open={showBoxAlert} onOpenChange={setShowBoxAlert}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-600">
              <AlertTriangle className="w-6 h-6" />
              Product Labels Printed - Box Sticker Required
            </DialogTitle>
            <DialogDescription>
              Product labels have been printed. Now select the box label printer and print box labels.
            </DialogDescription>
          </DialogHeader>
          
          {pendingBoxData && (
            <div className="py-4 space-y-4">
              <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                <div className="font-medium text-green-800">
                  Product Labels Printed
                </div>
                <div className="text-sm text-green-600">
                  {pendingBoxData.labelInfo.usn_count} combo USNs • {pendingBoxData.labelInfo.combo_name}
                </div>
              </div>
              
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                <div className="font-medium text-blue-800 mb-2">Box Label Printer</div>
                {boxPrinterDevice ? (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm text-blue-600">
                      <Printer className="w-4 h-4" />
                      <span>{boxPrinterDevice.productName || 'USB Printer'}</span>
                      <span className="text-green-600 font-medium">Connected</span>
                    </div>
                    <Button size="sm" variant="outline" onClick={connectBoxPrinter} className="text-blue-600 border-blue-300">
                      Change
                    </Button>
                  </div>
                ) : (
                  <Button onClick={connectBoxPrinter} variant="outline" className="w-full border-blue-300 text-blue-600 hover:bg-blue-100">
                    <Printer className="w-4 h-4 mr-2" />
                    Select Box Label Printer
                  </Button>
                )}
              </div>
              
              <Alert className="bg-yellow-50 border-yellow-300">
                <Package className="h-4 w-4 text-yellow-600" />
                <AlertTitle className="text-yellow-800">Ready to Print</AlertTitle>
                <AlertDescription className="text-yellow-700">
                  {boxPrinterDevice
                    ? 'Box label printer is connected. Click "Print Box Labels" to proceed.'
                    : 'Please select the printer with box sticker roll loaded.'}
                </AlertDescription>
              </Alert>
            </div>
          )}
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={handleBoxPrintCancel}>
              Skip Box Labels
            </Button>
            <Button
              onClick={handleBoxPrintConfirm}
              className="bg-orange-600 hover:bg-orange-700"
              disabled={!boxPrinterDevice}
              data-testid="print-box-labels-btn"
            >
              <Printer className="w-4 h-4 mr-2" />
              Print Box Labels
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Printer Settings Dialog */}
      <Dialog open={showPrinterSettings} onOpenChange={setShowPrinterSettings}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Printer Settings
            </DialogTitle>
            <DialogDescription>
              Configure USB printer for combo label printing
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-700">
                <strong>Template:</strong> Automatically selected based on the combo item's <code>print_template</code> field in Item Master.
              </p>
            </div>

            <div className="space-y-2">
              <Label>Pricing Type</Label>
              <Select value={pricingType} onValueChange={setPricingType}>
                <SelectTrigger data-testid="combo-pricing-type-select">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  {availablePricingTypes.map((pt) => (
                    <SelectItem key={pt} value={pt}>{pt}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Printer Model</Label>
              <Select value={printerModel} onValueChange={setPrinterModel}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(PRINTER_CONFIGS).map(([key, config]) => (
                    <SelectItem key={key} value={key}>{config.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Copies per Label</Label>
              <Input
                type="number"
                min="1"
                max="10"
                value={copiesPerLabel}
                onChange={(e) => setCopiesPerLabel(Math.max(1, parseInt(e.target.value) || 1))}
              />
            </div>
            
            <div className="p-3 bg-gray-100 rounded-lg space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Product Printer</span>
                {usbDevice ? (
                  <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300">
                    {usbDevice.productName || 'Connected'}
                  </Badge>
                ) : (
                  <Button size="sm" variant="outline" onClick={connectUSBPrinter}>
                    <Usb className="w-3 h-3 mr-1" /> Connect
                  </Button>
                )}
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Box Label Printer</span>
                {boxPrinterDevice ? (
                  <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300">
                    {boxPrinterDevice.productName || 'Connected'}
                  </Badge>
                ) : (
                  <Button size="sm" variant="outline" onClick={connectBoxPrinter}>
                    <Usb className="w-3 h-3 mr-1" /> Connect
                  </Button>
                )}
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button onClick={() => setShowPrinterSettings(false)} className="w-full">
              <Check className="w-4 h-4 mr-2" /> Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Excess Return USN Scanning Dialog */}
      <Dialog open={excessScanDialog} onOpenChange={(open) => { if (!returningExcess) setExcessScanDialog(open); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RotateCcw className="w-5 h-5" />
              Scan Excess USNs to Return to SFG
            </DialogTitle>
            <DialogDescription>
              Scan USNs that are being returned to SFG storage. Only the USNs NOT scanned here will be consumed for combo assembly.
            </DialogDescription>
          </DialogHeader>

          {loadingExcessData ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-6 h-6 animate-spin mr-2" />
              Loading picked USNs...
            </div>
          ) : (
            <div className="space-y-4">
              {/* Scan Input */}
              <div className="flex gap-2">
                <Input
                  placeholder="Scan USN barcode..."
                  value={excessScanInput}
                  onChange={(e) => setExcessScanInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleExcessScanUSN(excessScanInput);
                    }
                  }}
                  autoFocus
                  data-testid="excess-scan-input"
                />
                <Button
                  onClick={() => handleExcessScanUSN(excessScanInput)}
                  variant="outline"
                  data-testid="excess-scan-btn"
                >
                  Scan
                </Button>
              </div>

              {/* Component Summary */}
              {excessComponents.map((comp) => {
                const scannedSet = scannedReturnUsns[comp.component_barcode] || new Set();
                const scannedCount = scannedSet.size;
                const consumedCount = comp.total_picked_usns - scannedCount;
                
                return (
                  <Card key={comp.component_barcode} className="border">
                    <CardHeader className="py-3 px-4">
                      <CardTitle className="text-sm font-medium">{comp.component_name}</CardTitle>
                      <CardDescription className="text-xs">{comp.component_barcode}</CardDescription>
                    </CardHeader>
                    <CardContent className="px-4 pb-3">
                      <div className="grid grid-cols-3 gap-4 text-center mb-3">
                        <div>
                          <p className="text-xs text-gray-500">Total Picked</p>
                          <p className="text-lg font-bold" data-testid={`total-picked-${comp.component_barcode}`}>{comp.total_picked_usns}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Scanned (Returning)</p>
                          <p className="text-lg font-bold text-blue-600" data-testid={`scanned-return-${comp.component_barcode}`}>{scannedCount}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Will be Consumed</p>
                          <p className="text-lg font-bold text-orange-600" data-testid={`will-consume-${comp.component_barcode}`}>{consumedCount}</p>
                          <p className="text-xs text-gray-400">(needed: {comp.consumed_count})</p>
                        </div>
                      </div>
                      
                      {/* Scanned USNs list */}
                      {scannedCount > 0 && (
                        <div className="border rounded p-2 max-h-32 overflow-y-auto">
                          <p className="text-xs text-gray-500 mb-1">Scanned for return:</p>
                          <div className="flex flex-wrap gap-1">
                            {Array.from(scannedSet).map(usn => (
                              <Badge
                                key={usn}
                                variant="secondary"
                                className="text-xs cursor-pointer hover:bg-red-100"
                                onClick={() => {
                                  setScannedReturnUsns(prev => {
                                    const updated = { ...prev };
                                    const newSet = new Set(prev[comp.component_barcode]);
                                    newSet.delete(usn);
                                    updated[comp.component_barcode] = newSet;
                                    return updated;
                                  });
                                  toast.info(`Removed ${usn} from return list`);
                                }}
                              >
                                {usn} x
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}

              {/* Summary & Confirm */}
              <div className="border-t pt-3">
                <div className="flex items-center justify-between text-sm mb-3">
                  <span>
                    Total returning: <strong className="text-blue-600">
                      {Object.values(scannedReturnUsns).reduce((sum, set) => sum + set.size, 0)}
                    </strong> USNs
                  </span>
                  <span>
                    Total consuming: <strong className="text-orange-600">
                      {excessComponents.reduce((sum, c) => sum + c.total_picked_usns, 0) - Object.values(scannedReturnUsns).reduce((sum, set) => sum + set.size, 0)}
                    </strong> USNs
                  </span>
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setExcessScanDialog(false)}
              disabled={returningExcess}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmExcessReturn}
              disabled={returningExcess || loadingExcessData}
              className="bg-orange-600 hover:bg-orange-700"
              data-testid="confirm-excess-return-btn"
            >
              {returningExcess ? (
                <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Processing...</>
              ) : (
                <><Check className="w-4 h-4 mr-2" /> Confirm Return & Consume</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Leftover Declaration Dialog — auto-opens when session completes with unattributed pieces,
          or can be opened manually from Step 2 to declare which bundle has the excess pieces
          (available for all sessions with picked bundles, not just the ones the auto-prompt catches). */}
      <Dialog
        open={!!leftoverForm}
        onOpenChange={(open) => {
          if (open) return;
          if (submittingLeftover) return;
          if (manualLeftoverMode) {
            // Manual mode → user can close at will
            setLeftoverForm(null);
            setLeftoverSessionId(null);
            setLeftoverInputs({});
            setManualLeftoverMode(false);
          }
          // Auto mode → mandatory, cannot close
        }}
      >
        <DialogContent
          className="max-w-lg"
          onPointerDownOutside={(e) => { if (!manualLeftoverMode) e.preventDefault(); }}
          onEscapeKeyDown={(e) => { if (!manualLeftoverMode) e.preventDefault(); }}
          data-testid="leftover-declaration-dialog"
        >
          <DialogHeader>
            <DialogTitle>
              {manualLeftoverMode ? 'Declare Bundle with Excess Pieces' : 'Declare Leftover Pieces'}
            </DialogTitle>
            <DialogDescription>
              {manualLeftoverMode
                ? 'For each component, tell the system how many pieces physically remain on each bundle after this session. This routes downstream sessions to the correct bin and keeps inventory accurate.'
                : 'You were allocated more pieces than you assembled. Enter how many pieces physically remain on each bundle so downstream sessions are routed to the correct bin.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 max-h-[500px] overflow-y-auto">
            {(() => {
              const componentsToRender = manualLeftoverMode
                ? (leftoverForm?.components || [])
                : (leftoverForm?.components || []).filter(c => c.requires_declaration);
              if (componentsToRender.length === 0) {
                return (
                  <div className="border rounded-lg p-4 bg-amber-50 text-xs text-amber-800" data-testid="leftover-no-components">
                    No components available to declare for this session.
                    {manualLeftoverMode && ' Either all bundles have been fully consumed or no bundles have been picked yet.'}
                  </div>
                );
              }
              return componentsToRender.map((comp) => {
                const hasLeftover = comp.expected_leftover > 0;
                const sumForComp = (comp.bundles || []).reduce(
                  (acc, b) => acc + (parseInt(leftoverInputs[b.bundle_code], 10) || 0), 0
                );
                const ok = sumForComp === comp.expected_leftover;
                const singleBundle = (comp.bundles || []).length === 1;
                return (
                  <div key={comp.component_barcode} className={`border rounded-lg p-3 ${!hasLeftover ? 'bg-gray-100 opacity-70' : 'bg-gray-50'}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <p className="text-sm font-semibold">{comp.component_name}</p>
                        <p className="text-xs text-gray-500 font-mono">{comp.component_barcode}</p>
                      </div>
                      {hasLeftover ? (
                        <Badge className={ok ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}>
                          {sumForComp} / {comp.expected_leftover} pcs
                        </Badge>
                      ) : (
                        <Badge className="bg-gray-200 text-gray-700">No leftover</Badge>
                      )}
                    </div>
                    <p className="text-xs text-gray-600 mb-2">
                      Allocated <span className="font-semibold">{comp.total_allocated}</span> pcs,
                      {' '}consumed <span className="font-semibold">{comp.consumed_needed}</span> pcs,
                      {' '}leftover should total <span className="font-semibold">{comp.expected_leftover}</span> pcs.
                      {singleBundle && hasLeftover && manualLeftoverMode && (
                        <span className="block mt-1 text-indigo-700">Only 1 bundle picked — leftover auto-attributes to this bundle.</span>
                      )}
                    </p>
                    <div className="space-y-2">
                      {(comp.bundles || []).map((b) => (
                        <div key={b.bundle_code} className="flex items-center justify-between bg-white rounded px-2 py-2 border">
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-mono truncate" data-testid={`leftover-bundle-${b.bundle_code}`}>{b.bundle_code}</p>
                            <p className="text-[10px] text-gray-500">
                              {b.bin_location} · {b.original_qty} pcs
                              {b.shade ? ` · ${b.shade}` : ''}
                              {b.size ? ` · ${b.size}` : ''}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Input
                              type="number"
                              min={0}
                              max={b.original_qty}
                              value={leftoverInputs[b.bundle_code] ?? 0}
                              onChange={(e) => setLeftoverInputs(prev => ({ ...prev, [b.bundle_code]: e.target.value }))}
                              className="w-20 h-8 text-right"
                              disabled={!hasLeftover}
                              data-testid={`leftover-input-${b.bundle_code}`}
                            />
                            <span className="text-xs text-gray-500">pcs left</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              });
            })()}
          </div>
          <DialogFooter className="flex gap-2">
            {manualLeftoverMode && (
              <Button
                variant="outline"
                onClick={() => {
                  setLeftoverForm(null);
                  setLeftoverSessionId(null);
                  setLeftoverInputs({});
                  setManualLeftoverMode(false);
                }}
                disabled={submittingLeftover}
                data-testid="close-leftover-btn"
                className="flex-1"
              >
                Close
              </Button>
            )}
            <Button
              onClick={handleSubmitLeftover}
              disabled={submittingLeftover}
              data-testid="submit-leftover-btn"
              className={`${manualLeftoverMode ? 'flex-1' : 'w-full'} bg-green-600 hover:bg-green-700 text-white`}
            >
              {submittingLeftover ? 'Saving...' : 'Confirm Leftover & Continue'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>



      {/* Handover Suggestions Dialog */}
      <Dialog open={showHandoverDialog} onOpenChange={setShowHandoverDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Hand Over Excess Bundles</DialogTitle>
            <DialogDescription>
              Other sessions need the same components. Hand over directly to skip the return-to-SFG cycle.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-[400px] overflow-y-auto" data-testid="handover-suggestions-list">
            {handoverSuggestions.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-4">All bundles handed over. You can close this dialog.</p>
            ) : (
              handoverSuggestions.map((s, idx) => (
                <div key={`${s.target_session_id}-${s.component_barcode}`} className="border rounded-lg p-3 space-y-2 bg-blue-50 border-blue-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-blue-900">{s.component_name}</p>
                      <p className="text-xs text-blue-700 font-mono">{s.component_barcode}</p>
                    </div>
                    <Badge className="bg-amber-100 text-amber-800">{s.excess_pieces} excess pcs</Badge>
                  </div>
                  <div className="bg-white rounded p-2 border">
                    <p className="text-xs text-gray-500">Hand over to:</p>
                    <p className="text-sm font-semibold">{s.target_design_name} — {s.target_shade}/{s.target_size}</p>
                    {s.is_assigned ? (
                      <p className="text-xs text-gray-600">
                        Staff: <span className="font-medium">{s.target_staff_name || s.target_staff_email}</span>
                        {s.target_staff_id && <span className="text-gray-400 ml-1">({s.target_staff_id})</span>}
                      </p>
                    ) : (
                      <p className="text-xs text-amber-600 font-medium">Not yet assigned to staff</p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">Needs {s.target_needs} pcs &middot; {s.target_status === 'PENDING_COMPONENTS' ? 'Awaiting Components' : 'In Progress'}</p>
                  </div>
                  <div className="space-y-1">
                    {s.bundles.map(b => (
                      <div key={b.bundle_code} className="flex items-center justify-between bg-gray-50 rounded px-2 py-1">
                        <span className="text-xs font-mono">{b.bundle_code} ({b.usn_count} pcs)</span>
                        <Button
                          size="sm"
                          className="h-7 text-xs"
                          disabled={handingOver === b.bundle_code}
                          onClick={() => handleHandoverBundle(s, b.bundle_code)}
                          data-testid={`handover-btn-${idx}`}
                        >
                          {handingOver === b.bundle_code ? 'Handing Over...' : 'Hand Over'}
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowHandoverDialog(false)} data-testid="handover-skip-btn">
              {handoverSuggestions.length === 0 ? 'Close' : 'Skip & Return to SFG Later'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Session Dialog */}
      <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <DialogContent className="max-w-md" data-testid="cancel-session-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-700">Cancel Assembly Session</DialogTitle>
            <DialogDescription>
              This will cancel session <span className="font-mono text-xs">{cancelSessionTarget?.session_id}</span> for{' '}
              <strong>{cancelSessionTarget?.design_name || cancelSessionTarget?.combo_name}</strong>.
              All allocated bundles will be reverted to STORED and pick slips will be cancelled.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium text-gray-700">Reason (optional)</label>
              <Input
                placeholder="e.g., Components changed, wrong combo..."
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                className="mt-1"
                data-testid="cancel-reason-input"
              />
            </div>
            {cancelSessionTarget && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700 space-y-1">
                <p>This action will:</p>
                <ul className="list-disc list-inside text-xs space-y-0.5">
                  <li>Cancel all WMS pick slips for this session</li>
                  <li>Revert ALLOCATED/PICKED bundles back to STORED in SFG</li>
                  <li>Release any stranded floor stock (PENDING_PUTAWAY) to STORED</li>
                  <li>Mark the session as CANCELLED</li>
                </ul>
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setCancelDialogOpen(false); setCancelSessionTarget(null); setCancelReason(''); }} data-testid="cancel-dialog-dismiss-btn">
              Keep Session
            </Button>
            <Button
              variant="destructive"
              onClick={handleCancelSession}
              disabled={cancelling}
              data-testid="cancel-dialog-confirm-btn"
            >
              {cancelling ? <><RefreshCw className="w-3 h-3 mr-1 animate-spin" /> Cancelling...</> : 'Confirm Cancel'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Component Picker Dialog */}
      <Dialog open={assignPickerDialogOpen} onOpenChange={setAssignPickerDialogOpen}>
        <DialogContent className="max-w-sm" data-testid="assign-component-picker-dialog">
          <DialogHeader>
            <DialogTitle>Assign Picker</DialogTitle>
            <DialogDescription>
              Select an SFG picker for <span className="font-semibold">{assignPickerTarget?.component_name}</span>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Input
              placeholder="Search picker..."
              value={compPickerSearch}
              onChange={(e) => setCompPickerSearch(e.target.value)}
              className="h-8 text-sm"
              data-testid="comp-picker-search-input"
            />
            <div className="max-h-60 overflow-y-auto space-y-1">
              {loadingSfgPickers ? (
                <p className="text-center text-gray-400 text-sm py-4">Loading pickers...</p>
              ) : sfgPickers.filter(p => {
                if (!compPickerSearch.trim()) return true;
                const q = compPickerSearch.toLowerCase();
                return p.name.toLowerCase().includes(q) || p.staff_id.toLowerCase().includes(q);
              }).length === 0 ? (
                <p className="text-center text-gray-400 text-sm py-4">No SFG pickers found</p>
              ) : (
                sfgPickers.filter(p => {
                  if (!compPickerSearch.trim()) return true;
                  const q = compPickerSearch.toLowerCase();
                  return p.name.toLowerCase().includes(q) || p.staff_id.toLowerCase().includes(q);
                }).map((picker) => (
                  <button
                    key={picker.staff_id}
                    onClick={() => handleAssignComponentPicker(picker.staff_id)}
                    disabled={assigningPicker}
                    className="w-full flex items-center justify-between px-3 py-2 rounded-lg hover:bg-indigo-50 border border-transparent hover:border-indigo-200 transition-all text-left"
                    data-testid={`comp-picker-option-${picker.staff_id}`}
                  >
                    <div>
                      <p className="text-sm font-medium text-gray-900">{picker.name}</p>
                      <p className="text-xs text-gray-400">{picker.staff_id}{picker.designation ? ` · ${picker.designation}` : ''}</p>
                    </div>
                    <UserPlus className="w-4 h-4 text-indigo-400" />
                  </button>
                ))
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ComboAssemblyStation;
